import type { CompensationBreakdown } from '@/external/db/compensation-types';
import type { SourceEligibility } from '@/external/db/eligibility-types';
import type { PlanFeatures } from '@/external/db/plan-features-types';
import type {
  FieldResolution,
  NonstandardProvision,
  SourcedPlanRules,
} from '@/external/db/plan-rules-sourced-types';
import type { EntryDateProvision, EntryDateTiming, PlanRules } from '@/external/db/plan-rules-types';
import type { Remediation } from '@/external/db/remediation-types';
import {
  ColumnType,
  Generated,
  Insertable as KyselyInsertable,
  Selectable,
  Updateable as KyselyUpdateable,
} from 'kysely';

// App-facing row types. `Selectable` is Kysely's, re-exported. `Insertable` /
// `Updateable` omit workspace_id — the app.workspace_id GUC fills it on insert
// and app code doesn't control it. The pre-login / back-office paths that
// legitimately set workspace_id use the *Admin variants (Kysely's originals).
export type { Selectable } from 'kysely';
export type Insertable<T> = Omit<KyselyInsertable<T>, 'workspace_id'>;
export type Updateable<T> = Omit<KyselyUpdateable<T>, 'workspace_id'>;
export type InsertableAdmin<T> = KyselyInsertable<T>;
export type UpdateableAdmin<T> = KyselyUpdateable<T>;

// Shared column shapes. Nullable<T>: optional on insert (Kysely treats a
// nullable insert type as omittable). Json<T> / NullableJson<T>: jsonb columns
// — selected as the parsed shape, written as a JSON.stringify'd string.
export type Nullable<T> = ColumnType<T | null, T | null, T | null>;
export type Json<T> = ColumnType<T, string, string>;
export type NullableJson<T> = ColumnType<T | null, string | null, string | null>;

export type StageStatus = 'pending' | 'running' | 'review' | 'blocked' | 'done';

export type EmploymentStatus = 'active' | 'terminated' | 'retired';

export type UploadType = 'census' | 'payroll' | 'recordkeeper' | 'plan_doc' | 'w2';
export type UploadStatus = 'pending' | 'processing' | 'needs_mapping_review' | 'completed' | 'failed';

export type DistributionType = 'cash' | 'rollover' | 'hardship' | 'rmd' | 'death' | 'in_service';

export type ExceptionStatus = 'open' | 'resolved' | 'accepted';
export type ExceptionSeverity = 'info' | 'warning' | 'error';

export type TestKey =
  | '414s_ratio'
  | '410b_deferrals'
  | '410b_match'
  | 'adp'
  | 'acp'
  | '402g'
  | '415'
  | 'top_heavy';
export type TestStatus = 'pass' | 'fail' | 'not_run' | 'indeterminate';

export type CorrectionType = 'corrective_distribution' | 'qnec' | 'qmac' | 'lost_earnings' | 'refund';
export type CorrectionStatus = 'pending' | 'instruction_sent' | 'processed';

export type FilingStatus = 'draft' | 'signed' | 'submitted' | 'accepted';

export type EmailDirection = 'inbound' | 'outbound';
export type EmailAttachmentDisposition = 'unprocessed' | 'ingested' | 'skipped' | 'failed';

export type CrmContactRole =
  | 'plan_sponsor'
  | 'advisor'
  | 'recordkeeper'
  | 'payroll'
  | 'auditor'
  | 'custodian'
  | 'trustee'
  // Uncategorized — e.g. an inbound-email sender auto-allowlisted for routing.
  | 'other';

export type CrmProjectKind =
  | 'proposal'
  | 'plan_setup'
  | 'conversion'
  | 'termination'
  | 'amendment'
  | 'audit'
  | 'annual_admin';
export type CrmProjectStatus = 'open' | 'in_progress' | 'won' | 'lost' | 'completed' | 'on_hold';

export type DataSource = 'manual' | 'hubspot' | 'onboarding' | 'zoho' | 'shuttle' | 'schwab_forms';

// What kind of client this is — which console view the plan belongs on. Not to
// be confused with the plan's own lifecycle (active / terminated): a converting
// client's plan is active, and an existing client's plan can be terminated
// while we still owe its final filing. 'deactivated' means the relationship
// ended, not that the plan did.
export type ClientLifecycleStatus = 'existing' | 'conversion' | 'onboarding' | 'deactivated';

// crm_client_meta fields that can carry per-field provenance. Kept as
// an explicit union so consumers get autocomplete and a typo on a key
// is a tsc error, not a silent no-op.
export type FieldSourceKey =
  | 'custodian'
  | 'payroll_provider'
  | 'advisor_firm'
  | 'account_owner'
  | 'annual_fee'
  | 'plan_assets'
  | 'participants_eoy'
  | 'notes'
  | 'schwab_plan_id';

// Map of field name → DataSource on crm_client_meta (jsonb column).
// Only fields whose source is not 'manual' need to be tracked; missing
// keys default to manual.
export type FieldSources = Partial<Record<FieldSourceKey, DataSource>>;

export interface HubspotPipelineStage {
  id: string;
  label: string;
  displayOrder: number;
  metadata: {
    isClosed: boolean;
    probability: string;
  };
}

export interface ZohoPipelineStage {
  id: string;
  label: string;
  displayOrder: number;
  forecastCategory: string;
}

export type CallDirection = 'inbound' | 'outbound';
export type CallStatus = 'recording' | 'processing' | 'completed' | 'failed';

export interface CallActionItem {
  text: string;
  due_date?: string | null;
  owner?: string | null;
}

export type FtwStageKey =
  | 'census_load'
  | '410b_coverage'
  | 'adp_acp'
  | '402g_limits'
  | '415_additions'
  | 'top_heavy'
  | 'corrections'
  | 'form_assembly'
  | 'efast2_filing'
  | 'create_sponsor';

export type FtwActionType =
  | 'navigate'
  | 'set_field'
  | 'click'
  | 'read_value'
  | 'wait'
  | 'validation_error'
  | 'screenshot'
  | 'scroll'
  | 'key';

export type FtwSessionStatus = 'running' | 'completed' | 'failed' | 'aborted';

export type MatildaActionRisk = 'low' | 'high';
export type MatildaActionStatus = 'pending' | 'auto' | 'confirmed' | 'rejected' | 'failed';

export type SubagentModel = 'haiku' | 'sonnet' | 'opus';
export type SubagentStatus = 'spawning' | 'running' | 'awaiting_input' | 'completed' | 'failed' | 'aborted';
export type SubagentEmployerScope = 'all' | 'specific';
export type SubagentCreator = 'matilda' | 'user' | 'subagent';
export type SubagentMessageRole = 'user' | 'agent' | 'system' | 'matilda';

export const MONEY_SOURCES = [
  'pretax_deferral',
  'roth_deferral',
  'match',
  'nonelective',
  'profit_sharing',
  'qnec',
  'qmac',
  'catchup_pretax',
  'catchup_roth',
  'rollover_pretax',
  'rollover_roth',
] as const;
export type MoneySource = (typeof MONEY_SOURCES)[number];

export type OnboardingSessionStatus =
  | 'recording'
  | 'transcribing'
  | 'extracting'
  | 'review'
  | 'sent'
  | 'signed'
  | 'completed'
  | 'failed';
export type OnboardingChatRole = 'user' | 'agent';

type DateString = ColumnType<string, string, string>;
type Timestamp = ColumnType<Date, never, never>;

export interface EmployersRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  source: DataSource;
  hubspot_company_id: string | null;
  zoho_account_id: string | null;
  name: string;
  ein: string;
  plan_name: string;
  plan_number: string;
  form_type: string;
  active_plan_year_id: Generated<string | null>;
  active_payroll_connection_id: Generated<string | null>;
  active_recordkeeper_connection_id: Generated<string | null>;
  payroll_fetch_schedule: ColumnType<PayrollFetchSchedule | null, string | null | undefined, string | null>;
  lead_consultant_user_id: string;
  takeover_date: DateString | null;
  client_lifecycle_status: ClientLifecycleStatus;
  entity_type: string | null;
  sponsor_address: ColumnType<MailingAddress | null, string | null | undefined, string | null>;
  sponsor_phone: string | null;
  created_at: Timestamp;
}

export interface PlanYearsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  year_label: number;
  period_start: DateString;
  period_end: DateString;
  filing_deadline: DateString;
  prior_plan_year_id: Generated<string | null>;
  last_pipeline_job_id: Generated<string | null>;
  active_census_snapshot_id: Generated<string | null>;
  active_plan_document_id: Generated<string | null>;
  active_rk_deposits_extract_id: Generated<string | null>;
  active_rk_distributions_extract_id: Generated<string | null>;
  active_rk_loans_extract_id: Generated<string | null>;
  active_rk_balances_extract_id: Generated<string | null>;
  active_pipeline_run_id: Generated<string | null>;
  stage_payroll: StageStatus;
  stage_recordkeeper: StageStatus;
  stage_census: StageStatus;
  stage_plan_rules: StageStatus;
  stage_eligibility: StageStatus;
  stage_compensation: StageStatus;
  stage_reconciliation: StageStatus;
  stage_hce: StageStatus;
  stage_coverage_410b: StageStatus;
  stage_adp_acp: StageStatus;
  stage_limits_402g: StageStatus;
  stage_additions_415: StageStatus;
  stage_top_heavy: StageStatus;
  stage_corrections: StageStatus;
  stage_trust: StageStatus;
  stage_participant_counts: StageStatus;
  stage_form_assembly: StageStatus;
  stage_filing: StageStatus;
  ftw_compliance_built_at: Nullable<Date>;
  // The Compliance_TaskData VAR → value map the last build landed on FT
  // William, so a later build can tell its own write from somebody's edit.
  ftw_compliance_built_values: NullableJson<Record<string, string>>;
  created_at: Timestamp;
}

export type UploaderRole = 'sponsor' | 'tpa' | 'system';

export interface UploadsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  upload_type: UploadType;
  filename: string;
  // Ref into the uploadFiles storage location; repointed when an edited
  // version of the file is stored.
  storage_key: Nullable<string>;
  display_name: string | null;
  status: UploadStatus;
  error_message: string | null;
  uploaded_at: Timestamp;
  processed_at: Nullable<Date>;
  uploader_email: string | null;
  uploader_role: UploaderRole | null;
  content_sha256: Nullable<string>;
}

export interface MailingAddress {
  street1: string;
  street2: string | null;
  city: string;
  state: string;
  zip: string;
}

export type EDeliveryPreference = 'email' | 'paper';

export interface ParticipantsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  external_id: string;
  first_name: string;
  last_name: string;
  alternate_names: Json<string[]>;
  dob: DateString | null;
  hire_date: DateString | null;
  rehire_date: Generated<string | null>;
  term_date: DateString | null;
  employment_status: EmploymentStatus;
  ssn_fingerprint: Generated<string | null>;
  personal_email: Generated<string | null>;
  personal_phone: Generated<string | null>;
  mailing_address: ColumnType<MailingAddress | null, string | null | undefined, string | null>;
  e_delivery_preference: Generated<EDeliveryPreference | null>;
  contact_managed_at: Generated<Date | null>;
  ssn_encrypted: Generated<string | null>;
  identity_blind_index: Generated<string | null>;
  w2_record_id: string | null;
  // Officer status and direct ownership %, projected from key_participants by the
  // Shuttle sync (the HRIS facts the payroll feed doesn't carry). The compliance
  // pipeline reads these here, so it never reaches into the key_participants table.
  officer_flag: Generated<boolean | null>;
  ownership_pct_direct: Generated<number | null>;
}

export type W2ExtractionStatus = 'extracted' | 'failed';

export interface W2Box12Entry {
  code: string;
  amount: number;
}

export interface W2Box14Entry {
  label: string;
  amount: number | null;
  raw: string;
}

// Per-field confidence from the extraction model. Keys mirror the
// w2_records columns the model fills in. Missing keys → no confidence
// recorded (treat as low).
export type W2ExtractionConfidence = Partial<
  Record<
    | 'tax_year'
    | 'employer_ein'
    | 'employee_name'
    | 'box_1'
    | 'box_3'
    | 'box_5'
    | 'box_12'
    | 'box_13_retirement_plan'
    | 'box_14',
    number
  >
>;

export interface W2RecordsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  upload_id: string;
  participant_id: string | null;
  tax_year: number;
  employer_ein: string | null;
  employee_name: string | null;
  box_1: number | null;
  box_3: number | null;
  box_5: number | null;
  box_12: Json<W2Box12Entry[]>;
  box_13_retirement_plan: boolean | null;
  box_14: Json<W2Box14Entry[]>;
  extraction_confidence: Json<W2ExtractionConfidence>;
  extraction_status: W2ExtractionStatus;
  extraction_error: string | null;
  created_at: Timestamp;
  linked_at: ColumnType<Date | null, Date | null | undefined, Date | null | undefined>;
  reviewed_at: ColumnType<Date | null, Date | null | undefined, Date | null | undefined>;
  reviewed_by_user_id: string | null;
}

export type PlanDocType =
  | 'adoption_agreement'
  | 'summary_plan_description'
  | 'basic_plan_document'
  | 'amendment'
  | 'other';

export interface PlanDocumentsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  version: number;
  upload_id: string | null;
  effective_date: DateString;
  raw_text: string;
  amendments: Json<{ name: string; effective_date: string; summary: string }[]>;
  plan_rules: NullableJson<PlanRules>;
  plan_rules_sourced: NullableJson<SourcedPlanRules>;
  nonstandard_provisions: Json<NonstandardProvision[]>;
  doc_type: Nullable<PlanDocType>;
  amends_plan_document_id: Nullable<string>;
  executed_date: Nullable<string>;
  uploaded_at: Timestamp;
}

export type IngestBatchKind = 'census' | 'payroll' | 'recordkeeper' | 'plan_doc' | 'w2';
export type IngestBatchStatus = 'parsing' | 'needs_mapping_review' | 'failed' | 'completed';

export interface IngestBatchesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  upload_id: Generated<string | null>;
  export_id: Generated<string | null>;
  kind: IngestBatchKind;
  status: IngestBatchStatus;
  mapping_id: Generated<string | null>;
  row_count: Generated<number | null>;
  error_message: Generated<string | null>;
  superseded_by_batch_id: Generated<string | null>;
  created_at: Timestamp;
  completed_at: Generated<Date | null>;
}

export type MappingKind = 'census' | 'payroll' | 'recordkeeper';
export type RkSheetKind = 'deposits' | 'distributions' | 'loans' | 'balances';
export type MappingProposedBy = 'llm' | 'heuristic' | 'human';
export type MappingStatus = 'proposed' | 'approved' | 'rejected';

export interface ExtractionMappingsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  kind: MappingKind;
  sheet_kind: RkSheetKind | null;
  header_fingerprint: string;
  mapping: Json<Record<string, string>>;
  proposed_by: MappingProposedBy;
  status: MappingStatus;
  reviewed_by_user_id: Generated<string | null>;
  reviewed_at: Generated<Date | null>;
  created_at: Timestamp;
}

export type CensusSnapshotSource = 'census_upload' | 'approved_census_upload' | 'approved_payroll_change';

export interface CensusSnapshotsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  plan_year_id: string | null;
  version: number | null;
  content_hash: string | null;
  batch_id: string | null;
  source: CensusSnapshotSource;
  as_of_date: Generated<string | null>;
  row_count: number;
  created_at: Timestamp;
}

export interface CensusRowsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  snapshot_id: string;
  row_number: number;
  matched_participant_id: Generated<string | null>;
  external_id_raw: string | null;
  ssn_fingerprint: string | null;
  ssn_encrypted: string | null;
  identity_blind_index: string | null;
  first_name: string | null;
  last_name: string | null;
  dob: DateString | null;
  hire_date: DateString | null;
  rehire_date: DateString | null;
  term_date: DateString | null;
  employment_status_raw: string | null;
  ownership_pct_direct: number | null;
  ownership_pct_attributed: number | null;
  officer_flag: boolean | null;
  gross_comp_claimed: number | null;
  prior_year_comp_claimed: number | null;
  ytd_pretax_claimed: number | null;
  ytd_roth_claimed: number | null;
  ytd_match_claimed: number | null;
  balance_eoy_claimed: number | null;
  balance_at_determination_claimed: number | null;
  hours_claimed: number | null;
}

// Minimal participant roster shape captured on each immutable change item.
// Census financial/ownership claims stay in census_rows and are cloned from
// the base snapshot on approval; review JSON contains demographics only.
export interface CensusRosterState {
  first_name: string | null;
  last_name: string | null;
  dob: string | null;
  hire_date: string | null;
  rehire_date: string | null;
  term_date: string | null;
  employment_status: EmploymentStatus | null;
}

export interface CensusClaimState {
  ownership_pct_direct: number | null;
  ownership_pct_attributed: number | null;
  officer_flag: boolean | null;
  gross_comp_claimed: number | null;
  prior_year_comp_claimed: number | null;
  ytd_pretax_claimed: number | null;
  ytd_roth_claimed: number | null;
  ytd_match_claimed: number | null;
  balance_eoy_claimed: number | null;
  balance_at_determination_claimed: number | null;
  hours_claimed: number | null;
}

export type CensusChangeSetStatus =
  | 'proposed'
  | 'blocked'
  | 'approved'
  | 'rejected'
  | 'stale'
  | 'no_change';

export type CensusChangeItemKind =
  | 'initial_roster'
  | 'new_hire'
  | 'termination'
  | 'possible_termination'
  | 'rehire'
  | 'field_change'
  | 'ssn_added'
  | 'identity_match_required';

export interface CensusChangeSetsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  plan_year_id: string;
  source_pay_run_id: string | null;
  source_batch_id: string | null;
  source_snapshot_id: string | null;
  base_snapshot_id: string | null;
  result_snapshot_id: string | null;
  source_hash: string;
  status: CensusChangeSetStatus;
  change_count: number;
  blocker_count: number;
  complete_roster: boolean | null;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export interface CensusChangeItemsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  change_set_id: string;
  item_key: string;
  kind: CensusChangeItemKind;
  participant_id: string | null;
  before_state: NullableJson<CensusRosterState>;
  after_state: NullableJson<CensusRosterState>;
  changed_fields: Json<(keyof CensusRosterState)[]>;
  before_claims: NullableJson<CensusClaimState>;
  after_claims: NullableJson<CensusClaimState>;
  changed_claim_fields: Json<(keyof CensusClaimState)[]>;
  source_row_number: number | null;
  blocks_approval: boolean;
  blocker_reason: string | null;
  carried_from_change_set_id: string | null;
  observed_at: Timestamp;
}

export type CensusChangeApprovalDecision = 'approved' | 'rejected';
export type CensusChangeApprovalActorRole = 'tpa' | 'sponsor';

export interface CensusChangeApprovalsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  change_set_id: string;
  decision: CensusChangeApprovalDecision;
  actor_role: CensusChangeApprovalActorRole;
  actor_user_id: string | null;
  actor_email: string;
  note: string | null;
  decided_at: Timestamp;
}

export type CensusChangeType = 'added' | 'removed' | 'terminated' | 'rehired' | 'status_changed';

export interface CensusChangesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  snapshot_id: string;
  prior_snapshot_id: string;
  participant_id: string;
  change_type: CensusChangeType;
  prior_status: EmploymentStatus | null;
  new_status: EmploymentStatus | null;
  effective_date: DateString | null;
  detected_at: Timestamp;
}

export type ParticipantAliasSource = 'census' | 'payroll' | 'recordkeeper';

export interface ParticipantAliasesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  participant_id: string;
  source: ParticipantAliasSource;
  external_id: string;
  normalized_id: string;
  created_at: Timestamp;
}

export interface ShuttleIgnoredPlanCodesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  plan_code: string;
  ignored_through: DateString;
  created_at: Timestamp;
}

export interface IgnoredEmailSendersRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  sender_email: string;
  created_at: Timestamp;
}

export interface ShuttlePlanEmployersRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  plan_code: string;
  // The last cycle we expect this plan in an export. Set when an owner confirms a
  // 'shuttle_stopped' Today item; doubles as the acknowledged flag (non-null drops
  // the plan out of that branch).
  expected_through: Generated<string | null>;
  created_at: Timestamp;
}

export type ShuttleExportStatus = 'uploaded' | 'parsing' | 'parsed' | 'failed';

// File-level completeness over the zip alone. Per-employer ingestion is not an
// export property — it lives on ingest_batches and the sponsor page — so nothing
// derive-related is summarized here.
export interface ShuttleExportSummary {
  plans_in_index: number;
  indexed_but_missing: string[];
  present_but_unindexed: string[];
  duplicate_plan_codes: string[];
  cadence_gap_warning: string | null;
  // What the workbooks say, as opposed to which ones arrived. Null means nothing
  // looked at the content — a different claim from every list coming back empty.
  content_findings: ShuttleContentFindings | null;
}

export interface ShuttleContentFindings {
  unusable_identity_rows: ShuttleDroppedRowCount[];
  unusable_termination_rows: ShuttleDroppedRowCount[];
  contribution_flag_mismatches: ShuttleFlagMismatch[];
  earnings_flag_mismatches: ShuttleFlagMismatch[];
  schema_drift: ShuttleSchemaDrift[];
  terminations_without_workbook: string[];
}

export interface ShuttleDroppedRowCount {
  plan_code: string;
  rows: number[];
  total: number;
  dropped_amount: number;
}

export interface ShuttleFlagMismatch {
  plan_code: string;
  flagged: boolean;
  observed: boolean;
}

export interface ShuttleSchemaDrift {
  plan_code: string;
  missing_columns: ShuttleWorkbookColumn[];
}

export type ShuttleWorkbookColumn = (typeof SHUTTLE_WORKBOOK_COLUMNS)[number];

// The documented per-plan workbook layout (docs/shuttle-payroll-export.md §4). A
// column that disappears or gets renamed reads as null and coerces to zero, so a
// vanished money column is indistinguishable from a workbook where nobody
// contributed.
export const SHUTTLE_WORKBOOK_COLUMNS = [
  'NATIONAL_IDENTIFIER',
  'FIRST_NAME',
  'MIDDLE_NAMES',
  'LAST_NAME',
  'DATE_OF_BIRTH',
  'EMAIL_ADDRESS',
  'CELL_PHONE',
  'ADDRESS_LINE1',
  'ADDRESS_LINE2',
  'ADDRESS_LINE3',
  'CITY',
  'STATE',
  'POSTAL_CODE',
  'ORIGINAL_START_DATE',
  'REHIRE_DATE',
  'TERM_DATE',
  'YTD_GROSS_EARN',
  'HRS_WORKED',
  'EST_ANN_EARN',
  'N_401K_PRETAX',
  'N_401K_ROTH',
  'N_401K_AFTERTAX',
  'N_401K_CATCHUP_PT',
  'N_401K_CATCHUP_ROTH',
  'N_401K_EMPLOYER',
  'N_401K_LOAN',
  'ROTH_IRA',
  'ROTH_IRA_CATCHUP',
  'TRAD_IRA',
  'TRAD_IRA_CATCHUP',
  'SIMPLE_IRA',
  'SIMPLE_IRA_CATCHUP',
  'SIMPLE_IRA_EMPLOYER',
  'SIMPLE_IRA_ROTH',
] as const;

// Identifying aggregates over one plan's workbook, computed at parse so the
// unmapped-plan UI can describe a plan without opening the sealed artifact
// (the vault private key never reaches the web tier). Aggregates only — no
// names, SSNs, DoBs, or street addresses.
export interface ShuttlePlanPreview {
  roster_count: number;
  active_count: number;
  top_email_domains: { domain: string; count: number }[];
  top_locations: { city: string; state: string; count: number }[];
  earliest_hire_date: string | null;
}

export type PayrollConsolePullStatus = 'running' | 'completed' | 'failed';

export const PAYROLL_CONSOLE_PROVIDER_KEYS = ['gusto', 'qbo', 'netsuite', 'adp_run'] as const;
export type PayrollConsoleProvider = (typeof PAYROLL_CONSOLE_PROVIDER_KEYS)[number];

// Systems an authenticator can be bound to. Every payroll console is one, plus
// integrations that are not payroll consoles at all — the binding is about
// "which system's sign-in does this secret answer", not about payroll. One
// connection per provider per workspace is what lets an automated sign-in find
// its own code source with nothing to pick at run time.
export const AUTHENTICATOR_PROVIDER_KEYS = [...PAYROLL_CONSOLE_PROVIDER_KEYS, 'srt'] as const;
export type AuthenticatorProvider = (typeof AUTHENTICATOR_PROVIDER_KEYS)[number];

// One payroll-console pull attempt. Counterpart of shuttle_exports: the pay runs
// are the facts, this is the record that a pull happened — including the ones
// that wrote nothing.
export interface PayrollConsolePullsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  provider: PayrollConsoleProvider;
  company_name: string;
  session_id: string;
  status: PayrollConsolePullStatus;
  failure_reason: string | null;
  pay_dates: NullableJson<string[]>;
  lines: number | null;
  unmatched_lines: number | null;
  started_by_user_id: string | null;
  created_at: Timestamp;
  finished_at: Generated<Date | null>;
}

// A console provider's saved sign-in for one employer. `creds_ciphertext` is
// sealed with the payroll vault public key in the same `{values:{…}}` shape the
// playwright job carries, so the web pod never opens what it stores.
export interface PayrollConsoleCredentialsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  provider: PayrollConsoleProvider;
  creds_ciphertext: string;
  company_name: string;
  saved_by_user_id: string | null;
  created_at: Timestamp;
}

export const TOTP_ALGORITHM_KEYS = ['SHA1', 'SHA256', 'SHA512'] as const;
export type TotpAlgorithm = (typeof TOTP_ALGORITHM_KEYS)[number];
export type TotpDigits = 6 | 8;

// A workspace's enrolled TOTP connection ("authenticator app" entry). The
// secret is sealed with the payroll vault public key; only worker pods decrypt.
export interface TotpConnectionsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  label: string;
  issuer: string | null;
  account_name: string | null;
  provider: AuthenticatorProvider | null;
  secret_ciphertext: string;
  algorithm: TotpAlgorithm;
  digits: TotpDigits;
  period: number;
  created_by_user_id: string;
  created_at: Timestamp;
}

export interface ShuttleExportsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  batch_date: DateString;
  status: ShuttleExportStatus;
  summary: NullableJson<ShuttleExportSummary>;
  // Plan codes from the Index roster (every plan in the zip) and the subset that
  // carried a data workbook. Written at parse; drive the stopped/unmapped Today
  // branches without re-reading the sealed artifact.
  roster_plan_codes: Json<string[]>;
  workbook_plan_codes: Json<string[]>;
  // Previews keyed by plan code. Null for exports parsed before previews existed.
  plan_previews: NullableJson<Record<string, ShuttlePlanPreview>>;
  source_zip_storage_key: Nullable<string>;
  // Sealed per-plan artifact refs by plan code; a missing entry means the
  // plan has no artifact in this export.
  plan_storage_keys: NullableJson<Record<string, string>>;
  superseded_by_export_id: Generated<string | null>;
  parse_started_at: Generated<Date | null>;
  parse_attempt_id: Generated<string | null>;
  uploaded_by_user_id: string;
  created_at: Timestamp;
}

export interface ShuttleEmployerSyncReceiptsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  export_id: string;
  completed_at: Timestamp;
}

export type PayRunSource = 'file_upload' | 'connector' | 'legacy' | 'shuttle';
export type PayRunKind = 'regular' | 'bonus' | 'reversal';

export interface PayRunsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  batch_id: string | null;
  source: PayRunSource;
  source_ref: string;
  source_content_hash: Generated<string | null>;
  source_root_ref: Generated<string | null>;
  superseded_by_pay_run_id: Generated<string | null>;
  pay_date: DateString;
  period_start: DateString | null;
  period_end: DateString | null;
  kind: PayRunKind;
  created_at: Timestamp;
}

export interface PayRunLinesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  pay_run_id: string;
  participant_id: string | null;
  external_id_raw: string | null;
  first_name_raw: string | null;
  last_name_raw: string | null;
  gross_comp: number | null;
  ytd_gross_observed: number | null;
  est_annual_earn: number | null;
  hours: number | null;
  pretax: number;
  roth: number;
  after_tax: number;
  catchup_pretax: number;
  catchup_roth: number;
  employer_match: number;
  employer_nonelective: number;
  employer_unallocated: number;
  loan_repayment: number;
}

export type RkExtractSource = 'file_upload' | 'legacy';
export type RkDepositMoneySource = 'employee_pretax' | 'employee_roth' | 'employer_match' | 'aggregate';
export type BalanceObservationKind = 'eoy' | 'determination' | 'vested';

export interface RkExtractsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  batch_id: string | null;
  source: RkExtractSource;
  has_deposits: boolean;
  has_distributions: boolean;
  has_loans: boolean;
  has_balances: boolean;
  created_at: Timestamp;
}

export interface RkDepositsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  extract_id: string;
  pay_run_id: string | null;
  participant_id: string | null;
  external_id_raw: string | null;
  pay_date: DateString | null;
  deposit_date: DateString;
  money_source: RkDepositMoneySource;
  amount: number;
}

export interface RkBalanceObservationsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  extract_id: string;
  participant_id: string;
  kind: BalanceObservationKind;
  value: number;
  as_of_date: Generated<string | null>;
  created_at: Timestamp;
}

export type PlanDocExtractionStatus = 'extracted' | 'failed' | 'approved' | 'rejected';

// Staging for Opus plan-document output, mutable until approved. The name is
// the contract: after approval the rules live on the plan_documents version
// row — nothing outside the approval flow reads this table.
export interface PreApprovedPlanDocumentExtractionsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  upload_id: string;
  batch_id: string;
  status: PlanDocExtractionStatus;
  summary_markdown: string | null;
  proposed_plan_rules: NullableJson<SourcedPlanRules>;
  proposed_version_label: string | null;
  proposed_effective_date: Generated<string | null>;
  proposed_executed_date: Nullable<string>;
  amendments: Json<{ name: string; effective_date: string; summary: string }[]>;
  uncertainties: Json<string[]>;
  nonstandard_provisions: Json<NonstandardProvision[]>;
  field_resolutions: NullableJson<FieldResolution[]>;
  kpc_conflict_count: Nullable<number>;
  missing_field_count: Generated<number>;
  doc_type: Nullable<PlanDocType>;
  amends_plan_document_id: Nullable<string>;
  // Dotted rule paths an amendment overlay changed, so review can badge them.
  amended_paths: NullableJson<string[]>;
  extraction_error: string | null;
  reviewed_by_user_id: Generated<string | null>;
  reviewed_at: Generated<Date | null>;
  approved_plan_document_id: Generated<string | null>;
  created_at: Timestamp;
}

export type PipelineRunStatus = 'running' | 'completed' | 'failed';
export type TestResultOrigin = 'precheck' | 'ftw';

export interface PipelineRunsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  plan_year_id: string;
  status: PipelineRunStatus;
  census_snapshot_id: string | null;
  plan_document_id: string | null;
  rk_deposits_extract_id: string | null;
  rk_distributions_extract_id: string | null;
  rk_loans_extract_id: string | null;
  rk_balances_extract_id: string | null;
  triggered_by_user_id: string | null;
  job_id: string | null;
  started_at: Timestamp;
  finished_at: Generated<Date | null>;
}

export interface PipelineRunPayrollBatchesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  pipeline_run_id: string;
  ingest_batch_id: string;
}

export interface ParticipantComputationsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  pipeline_run_id: string;
  participant_id: string;
  eligibility_status: string | null;
  entry_date: Generated<string | null>;
  eligibility_by_source: NullableJson<SourceEligibility[]>;
  key_flag: boolean | null;
  officer_flag: boolean | null;
  ownership_pct_direct: number | null;
  ownership_pct_attributed: number | null;
  gross_comp: number | null;
  prior_year_comp: number | null;
  gross_comp_payroll: number | null;
  plan_comp: number | null;
  safeharbor_comp: number | null;
  comp_415: number | null;
  comp_eligible_deferral: number | null;
  comp_eligible_match: number | null;
  comp_eligible_profit_sharing: number | null;
  comp_capped: boolean | null;
  comp_breakdown: NullableJson<CompensationBreakdown>;
  balance_eoy: number | null;
  balance_at_determination: number | null;
  deferral_pretax: number;
  deferral_roth: number;
  after_tax: number;
  catchup_pretax: number;
  catchup_roth: number;
  employer_match: number;
  employer_nonelective: number;
  employer_unallocated: number;
  loan_repayments: number;
  hours: number;
}

export interface DistributionsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  participant_id: string;
  rk_extract_id: string;
  distribution_date: DateString;
  distribution_type: DistributionType;
  gross_amount: number;
  tax_withheld: number;
  money_source_breakdown: Json<Partial<Record<MoneySource, number>>>;
}

export interface LoansRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  participant_id: string;
  rk_extract_id: string;
  origination_date: DateString;
  original_amount: number;
  interest_rate: number;
  term_months: number;
  outstanding_balance: number;
  in_default: boolean;
}

export interface TrustRollforwardRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  pipeline_run_id: string;
  plan_year_id: string;
  line_key: string;
  line_label: string;
  amount: number;
  display_order: number;
}

export interface TestResultsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  pipeline_run_id: string;
  plan_year_id: string;
  origin: TestResultOrigin;
  test_key: TestKey;
  status: TestStatus;
  summary: Json<Record<string, unknown>>;
  per_participant: Json<Record<string, unknown>[]>;
  run_at: Timestamp;
}

// Per-exception structured context — record IDs, raw values, row numbers.
// COG-333 — one row per run of the continuous payroll compliance monitor.
// Append-only, workspace-scoped, plan-year independent (window derived from the
// triggering pay run's pay_date).
export type PayrollCycleRunStatus = 'running' | 'completed' | 'failed';

export interface PayrollCycleRunsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  pay_run_id: string;
  window_start: DateString;
  window_end: DateString;
  status: PayrollCycleRunStatus;
  finding_count: Generated<number>;
  error_count: Generated<number>;
  warning_count: Generated<number>;
  info_count: Generated<number>;
  created_count: Generated<number>;
  resolved_count: Generated<number>;
  // Check ids that had no source data to validate against this cycle; null on
  // pre-column rows (treated as fully checked).
  unchecked_check_ids: ColumnType<string[] | null, string | null | undefined, string | null>;
  // The full check roster as of run time — without it, "not unchecked" cannot
  // distinguish a passed check from one that didn't exist yet.
  evaluated_check_ids: ColumnType<string[] | null, string | null | undefined, string | null>;
  // Immutable evidence from this monitor cycle, frozen at completion; the
  // exceptions working set is replaced each cycle and cannot serve as history.
  findings: NullableJson<PayrollCycleFindingRecord[]>;
  error_message: string | null;
  started_at: Timestamp;
  finished_at: Generated<Date | null>;
}

export interface PayrollCycleFindingRecord {
  severity: ExceptionSeverity;
  kind: string;
  description: string;
  affected_count: number;
}

// Durable ledger of SchARP payroll submissions — one row per payroll file, in
// two stages. Stage 1 (build): the file is generated, validated, and stored;
// the row rests at `built` while a human moves the file to Schwab's NAS (SFTP
// later). Stage 2 (submit): the row is claimed BEFORE the API call so a crash
// can never lose the fact that a submission may have happened; unknown
// outcomes resolve by querying Schwab, never by resubmitting. `refused` rows
// (build-stage validation failures, no file worth delivering) are the only
// ones excluded from the one-per-(plan, payroll date) unique guard.
export type SchwabPayrollSubmissionStatus =
  | 'refused'
  | 'built'
  | 'claimed'
  | 'submitted'
  | 'parked'
  | 'launch_claimed'
  | 'launched'
  | 'awaiting_match_approval'
  | 'failed';

export interface SchwabPayrollSubmissionsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  pay_run_id: string;
  plan_code: string;
  schwab_plan_idi: Nullable<number>;
  payroll_date: DateString;
  // UNC path handed to srtProcessPayrollFile (the API reads from Schwab's NAS;
  // it never carries file content).
  file_path: string;
  // Our stored copy of the exact bytes submitted.
  file_storage_key: string;
  sheet_name: string;
  template_ids: Json<number[]>;
  participant_count: number;
  // Sum of every money bucket in the file (contributions + loan repayments).
  total_amount: number;
  status: SchwabPayrollSubmissionStatus;
  // Verbatim Schwab Status enum value from the latest poll; `status` above is
  // our lifecycle, this is theirs.
  last_schwab_status: Nullable<string>;
  schwab_exceptions: Nullable<boolean>;
  schwab_event_id: Nullable<number>;
  error: Nullable<string>;
  submitted_at: Nullable<Date>;
  parked_at: Nullable<Date>;
  launched_at: Nullable<Date>;
  match_gate_at: Nullable<Date>;
  created_at: Timestamp;
}

// Per-workspace SchARP payroll API service account. Password is payroll-vault
// ciphertext (decrypts only on worker pods); one row per workspace, absent
// until credentials are entered. Mirrors docusign_credentials.
export interface SchwabApiCredentialsRow {
  workspace_id: Generated<string>;
  username: string;
  password_ciphertext: string;
  last_test_at: Nullable<Date>;
  last_test_ok: boolean | null;
  last_test_error: string | null;
  created_at: ColumnType<Date, never, never>;
  updated_at: ColumnType<Date, Date | undefined, Date | undefined>;
}

// Append-only audit log of every SchARP API request/response (credentials
// redacted) — ERISA money movement must be reconstructable.
export interface SchwabApiCallsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  submission_id: string;
  operation: string;
  request_xml: string;
  response_status: Nullable<number>;
  response_xml: Nullable<string>;
  error: Nullable<string>;
  duration_ms: number;
  created_at: Timestamp;
}

// One Schwab eligibility requirement as Schwab's plan-setup form states it.
// The definitions and hours methods are Schwab's own codes, carried verbatim.
export interface SchwabEligibilityRequirement {
  name: string;
  description: string;
  first_year_definition: string;
  other_years_definition: string;
  entry_cycle: EntryDateProvision;
  minimum_age_months: number;
  minimum_months: number;
  minimum_hours: number;
  hours_method: string | null;
  consecutive_periods: number | null;
  entry_date: EntryDateTiming | null;
}

// Percent-denominated, unlike PlanRules.MatchTier, which stores fractions.
export interface SchwabMatchTier {
  rate_pct: number;
  up_to_pct: number;
}

export interface SchwabVestingGradedRow {
  time_increment: number;
  vested_pct: number;
}

export type SchwabVestingGradedRows = Partial<
  Record<'safe_harbor' | 'profit_sharing', SchwabVestingGradedRow[]>
>;

export type SchwabPlanSetupSource = 'matilda' | 'zoho' | 'manual' | 'default' | 'ach_form';

export type SchwabPlanSetupField =
  | 'schwab_plan_id'
  | 'eligibility_requirements'
  | 'match_tiers'
  | 'vesting_safe_harbor'
  | 'vesting_profit_sharing'
  | 'vesting_graded_rows'
  | 'pay_cycle'
  | 'pay_cycle_secondary'
  | 'pay_cycle_start_date'
  | 'ach'
  | 'ach_account_nickname'
  | 'default_investments';

export type SchwabPlanSetupFieldSources = Partial<Record<SchwabPlanSetupField, SchwabPlanSetupSource>>;

export interface SchwabPlanSetupRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  schwab_plan_id: string | null;
  eligibility_requirements: NullableJson<SchwabEligibilityRequirement[]>;
  match_tiers: NullableJson<SchwabMatchTier[]>;
  vesting_safe_harbor: string | null;
  vesting_profit_sharing: string | null;
  vesting_graded_rows: NullableJson<SchwabVestingGradedRows>;
  pay_cycle: string | null;
  pay_cycle_secondary: string | null;
  pay_cycle_start_date: DateString | null;
  ach_bank_name: string | null;
  ach_account_type: string | null;
  ach_account_nickname: string | null;
  ach_routing_ciphertext: string | null;
  ach_account_ciphertext: string | null;
  ach_account_last4: string | null;
  // Reserved for the investment lineup, which has no agreed shape yet — the
  // fill writes null, so nothing may be read out of it.
  default_investments: NullableJson<never[]>;
  field_sources: Json<SchwabPlanSetupFieldSources>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export type SrtSetupRunMode = 'verify' | 'apply';
export type SrtSetupRunStatus = 'running' | 'completed' | 'cancelled';
export type SrtSetupRunPlanStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

// The eight Plan Maintenance setup items a run can cover, in screen order.
export type SrtSetupItemId =
  | 'eligibility'
  | 'pay_cycles'
  | 'template_settings'
  | 'payroll_funding'
  | 'default_investments'
  | 'match_provisions'
  | 'vesting_controls'
  | 'report_format';

// One entry per item the session completed, in execution order. `ok` = matches
// the expected state; `changed` = apply mode wrote it; `mismatch` = verify mode
// found a deviation; `blocked` = the screen refused (missing prerequisite).
export interface SrtSetupItemResult {
  item: SrtSetupItemId;
  status: 'ok' | 'changed' | 'mismatch' | 'blocked' | 'error';
  detail: string;
  // Set when apply skipped the item because its source data is absent — the
  // field names a human has to fill in (e.g. the ACH numbers Zoho lacks).
  // Aggregated into the run's missing-data report.
  missing_fields?: string[];
}

export interface SrtSetupRunsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  started_by_user_id: string;
  mode: SrtSetupRunMode;
  items: Json<SrtSetupItemId[]>;
  status: SrtSetupRunStatus;
  created_at: Timestamp;
}

export interface SrtSetupRunPlansRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  run_id: string;
  employer_id: string;
  plan_code: string;
  status: SrtSetupRunPlanStatus;
  item_results: NullableJson<SrtSetupItemResult[]>;
  uvc_session_id: Nullable<string>;
  recording_storage_key: Nullable<string>;
  error: Nullable<string>;
  started_at: Nullable<Date>;
  finished_at: Nullable<Date>;
  created_at: Timestamp;
}

// The two-credential Citrix chain into record keeping (not the SchARP API
// service account, which is schwab_api_credentials): the StoreWeb workforce
// sign-in (KPC.-prefixed username + password + PingID) and the Schwab RT
// Recordkeeping SQL application login (operator + password). Passwords are
// payroll-vault ciphertext; the PingID TOTP secret lives in totp_connections,
// referenced here. One row per workspace, absent until entered.
export interface SrtPortalCredentialsRow {
  workspace_id: Generated<string>;
  username: string;
  password_ciphertext: string;
  rk_operator: string;
  rk_password_ciphertext: string;
  created_at: ColumnType<Date, never, never>;
  updated_at: ColumnType<Date, Date | undefined, Date | undefined>;
}

// Discriminated only loosely so each detector can attach whatever shape it
// has on hand; consumers (remediations.ts) read by key with null-safety.
export type ExceptionRawData = Record<string, unknown>;

export interface ExceptionsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  plan_year_id: Generated<string | null>;
  batch_id: Generated<string | null>;
  source_stage: string;
  severity: ExceptionSeverity;
  description: string;
  status: ExceptionStatus;
  affected_participant_ids: Json<string[]>;
  variance_amount: number | null;
  resolution_note: string | null;
  fix_suggestion: string | null;
  raw_data: NullableJson<ExceptionRawData>;
  proposed_remediation: NullableJson<Remediation>;
  created_at: Timestamp;
}

export interface CorrectionsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  plan_year_id: string;
  pipeline_run_id: Generated<string | null>;
  correction_type: CorrectionType;
  participant_id: string;
  money_source: MoneySource | null;
  amount: number;
  deadline: DateString | null;
  status: CorrectionStatus;
  instruction_sent_at: Nullable<Date>;
}

export interface FilingsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  plan_year_id: string;
  status: FilingStatus;
  lines: Json<
    { line_key: string; label: string; value: string | number | null; citation: string | null }[]
  >;
  signers: Json<{ role: string; name: string; signed_at: string | null }[]>;
  signed_at: Nullable<Date>;
  efast2_ack_id: string | null;
  submitted_at: Nullable<Date>;
}

// Reintroducing a driver means widening this union AND the DB CHECK together
// (the manifest's enumChecks ties them). 'ftwlink' is the HTTP API path, which
// has no browser session and therefore no action log or recording.
export type FtwDriverKind = 'playwright' | 'ftwlink';

export interface FtwSessionsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  stage_key: FtwStageKey;
  status: FtwSessionStatus;
  ftw_plan_id: string | null;
  started_at: Timestamp;
  ended_at: Nullable<Date>;
  result_summary: NullableJson<Record<string, unknown>>;
  failure_reason: string | null;
  recording_url: string | null;
  driver_kind: FtwDriverKind;
  subagent_id: Generated<string | null>;
}

export interface FtwActionLogRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  session_id: string;
  employer_id: string;
  stage_key: FtwStageKey;
  step_number: number;
  occurred_at: Timestamp;
  action_type: FtwActionType;
  page_url: string;
  selector: string | null;
  payload: ColumnType<unknown, string | null, string | null>;
  prior_value: ColumnType<unknown, string | null, string | null>;
  notes: string | null;
  screenshot_url: string | null;
}

export type FtwAuthMethod = 'username_password' | 'sso';

// FTW credential vault rows. Columns hold ciphertext only — the encrypt /
// decrypt scheme lives in the ftwilliam domain (`@/domains/ftwilliam/ftw-vault`),
// not in the DB layer. Queries here read and write the opaque blobs; the
// domain caller decrypts after reading and encrypts before writing.
export interface FtwSharedCredentialsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  auth_method: ColumnType<FtwAuthMethod, FtwAuthMethod, FtwAuthMethod>;
  username_ciphertext: string | null;
  password_ciphertext: string | null;
  company_code_ciphertext: string | null;
  storage_state_ciphertext: string | null;
  key_id: string;
  created_at: ColumnType<Date, never, never>;
  rotated_at: Nullable<Date>;
}

// Legacy per-employer credential table — kept for backwards compatibility
// with rows written before the shared-account migration. New writes go to
// ftw_shared_credentials; this type stays so any read path that still
// references it compiles.
export interface FtwCredentialsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  auth_method: ColumnType<FtwAuthMethod, FtwAuthMethod, FtwAuthMethod>;
  username_ciphertext: string | null;
  password_ciphertext: string | null;
  company_code_ciphertext: string | null;
  storage_state_ciphertext: string | null;
  key_id: string;
  created_at: ColumnType<Date, never, never>;
  rotated_at: Nullable<Date>;
}

// FTWLink API-key vault row. One per workspace; holds the API key as
// ciphertext (same asymmetric sealed-box vault as the shared credentials). A
// row exists only once a key is set; api_id is nullable (set separately).
export interface FtwLinkCredentialsRow {
  workspace_id: Generated<string>;
  api_key_ciphertext: string;
  key_id: string;
  api_id: string | null;
  created_at: ColumnType<Date, never, never>;
  updated_at: ColumnType<Date, Date | undefined, Date | undefined>;
}

export interface DocusignCredentialsRow {
  workspace_id: Generated<string>;
  integration_key: string;
  account_id: string;
  user_id: string;
  account_base_uri: string;
  oauth_base: string;
  rsa_private_key_ciphertext: string;
  connect_hmac_secret: string | null;
  countersigner_name: string | null;
  countersigner_email: string | null;
  last_test_at: Nullable<Date>;
  last_test_ok: boolean | null;
  last_test_error: string | null;
  created_at: ColumnType<Date, never, never>;
  updated_at: ColumnType<Date, Date | undefined, Date | undefined>;
}

// Pre-generated Schwab Recordkeeper Change Forms ((k)PC batch), matched to an
// employer once at conversion time and persisted. Retires when real Schwab
// form generation lands.
export interface SchwabFormsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  code: string;
  plan_name: string;
  ein: string | null;
  storage_key: string;
  matched_employer_id: string | null;
  created_at: Timestamp;
}

export interface ConversionSignersRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  schwab_plan_id: string;
  account_name: string;
  first_name: string;
  last_name: string;
  email: string;
  title: string;
  notes: string | null;
  synced_at: Timestamp;
}

export interface AgentMailConfig {
  inbox_id: string;
  address: string;
  webhook_id: string | null;
  webhook_secret: string | null;
}

export interface WorkspacesRow {
  id: Generated<string>;
  name: string;
  slug: string;
  default_lead_consultant_user_id: Generated<string | null>;
  default_lead_consultant_email: Generated<string | null>;
  matilda_voice: string | null;
  agentmail_config: NullableJson<AgentMailConfig>;
  stage_stall_threshold_days: number;
  created_at: Timestamp;
}

export type WorkspaceRole = 'owner' | 'member';

export type UserStatus = 'active' | 'deactivated';

export interface UsersRow {
  id: Generated<string>;
  workspace_id: string;
  email: string;
  role: WorkspaceRole;
  status: UserStatus;
  name: string;
  created_at: Timestamp;
  last_login_at: Nullable<Date>;
}

export interface WorkspaceInvitesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  email: string;
  role: WorkspaceRole;
  invited_by_user_id: string;
  invited_at: Generated<Date>;
  expires_at: Date;
}

export interface GmailConnectionsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  // 'personal' = a user's own Gmail (threads land in their @me); 'shared' = the
  // workspace's common @matilda inbox (synced threads have a NULL owner). For a
  // shared connection user_id holds the connecting owner (also connected_by_user_id).
  kind: 'personal' | 'shared';
  user_id: string;
  connected_by_user_id: string | null;
  mailbox_id: string | null;
  gmail_address: string;
  refresh_token_ciphertext: string;
  history_id: string | null;
  last_run_start_at: Nullable<Date>;
  last_success_at: Nullable<Date>;
  failed_at: Nullable<Date>;
  consecutive_failures: Generated<number>;
  // Google rejected the refresh token (invalid_grant) — the grant is dead
  // until the user re-consents; sync/send skip the connection meanwhile.
  auth_revoked_at: Nullable<Date>;
  disconnected_at: Nullable<Date>;
  created_at: Timestamp;
}

export interface SubagentsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  parent_id: string | null;
  created_by: SubagentCreator;
  name: string;
  goal: string;
  completion_criteria: string;
  model: SubagentModel;
  tool_allowlist: Json<string[]>;
  employer_scope: SubagentEmployerScope;
  ftw_enabled: boolean;
  status: SubagentStatus;
  result_summary: string | null;
  failure_reason: string | null;
  system_prompt: string;
  max_turns: number;
  max_seconds: number;
  parent_employer_id: Generated<string | null>;
  parent_stage_slug: Generated<string | null>;
  parent_global_user_id: Generated<string | null>;
  parent_global_surface: Generated<string | null>;
  created_at: Timestamp;
  started_at: Nullable<Date>;
  ended_at: Nullable<Date>;
  updated_at: Generated<Date>;
}

export interface SubagentEmployersRow {
  workspace_id: Generated<string>;
  subagent_id: string;
  employer_id: string;
}

export interface SubagentChatMessagesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  subagent_id: string;
  seq: Generated<number>;
  role: SubagentMessageRole;
  body: string;
  actions: ColumnType<unknown, string | null, string | null>;
  created_at: Timestamp;
}

export interface GlobalChatMessagesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  user_id: string;
  surface: ColumnType<'global', 'global', 'global'>;
  role: ColumnType<'user' | 'agent', 'user' | 'agent', 'user' | 'agent'>;
  agent: string | null;
  body: string;
  actions: ColumnType<unknown, string | null, string | null>;
  // bigserial — tiebreaker for ordering when two rows share a created_at us.
  seq: Generated<number>;
  created_at: Timestamp;
}

export interface CrmClientMetaRow {
  workspace_id: Generated<string>;
  employer_id: string;
  advisor_firm: string | null;
  custodian: string | null;
  payroll_provider: string | null;
  payroll_integration_type: 'api' | 'console' | 'upload' | null;
  account_owner: string | null;
  annual_fee: number | null;
  plan_assets: number | null;
  participants_eoy: number | null;
  contract_start: DateString | null;
  contract_renewal: DateString | null;
  last_touch_at: Nullable<Date>;
  notes: string | null;
  schwab_plan_id: string | null;
  field_sources: Json<FieldSources>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export interface CrmContactsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  role: CrmContactRole;
  name: string;
  email: string | null;
  phone: string | null;
  firm_name: string | null;
  title: string | null;
  is_primary: boolean;
  source: DataSource;
  hubspot_contact_id: string | null;
  zoho_contact_id: string | null;
  created_at: Timestamp;
}

export interface CrmProjectsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  kind: CrmProjectKind;
  name: string;
  status: CrmProjectStatus;
  owner: string | null;
  due_date: DateString | null;
  value: number | null;
  notes: string | null;
  source: DataSource;
  hubspot_deal_id: string | null;
  zoho_deal_id: string | null;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export interface HubspotCredentialsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  portal_id: string;
  access_token: string;
  refresh_token: string;
  token_expires_at: ColumnType<Date, Date, Date>;
  scopes: string[];
  // Empty array = "do not import". Non-empty = "auto-import won deals
  // from these pipelines". The OAuth flow defaults this to every
  // pipeline in the org (i.e. "Select all is on" out of the box).
  selected_pipeline_ids: string[];
  connected_at: Timestamp;
  disconnected_at: Nullable<Date>;
  connected_by_user_id: string | null;
  last_backfill_at: Nullable<Date>;
  last_backfill_stats: NullableJson<Record<string, number>>;
}

export interface HubspotPipelinesRow {
  workspace_id: Generated<string>;
  pipeline_id: string;
  label: string;
  stages: Json<HubspotPipelineStage[]>;
  updated_at: Generated<Date>;
}

export interface TodayFactSnapshotsRow {
  workspace_id: Generated<string>;
  fact_id: string;
  snapshot_date: DateString;
  count: number;
  picked_for_card: boolean;
  headline: string | null;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export interface HubspotCompaniesRow {
  workspace_id: Generated<string>;
  hubspot_company_id: string;
  name: string | null;
  domain: string | null;
  lifecycle_stage: string | null;
  is_customer: boolean;
  employer_id: string | null;
  properties: Json<Record<string, unknown>>;
  primary_contact_email: string | null;
  account_owner: string | null;
  last_synced_at: Generated<Date>;
}

export interface ZohoCredentialsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  zoho_org_id: string;
  // Zoho is multi-datacenter; both endpoints come from the OAuth
  // callback and every later call must use them.
  accounts_server: string;
  api_domain: string;
  access_token: string;
  refresh_token: string;
  token_expires_at: ColumnType<Date, Date, Date>;
  scopes: string[];
  // Empty array = "do not import won deals". Same semantics as the
  // HubSpot column; the customer-designation gate is independent of it.
  selected_pipeline_ids: string[];
  // pg returns bigint as string.
  watch_channel_id: string | null;
  watch_token: string | null;
  watch_expires_at: Nullable<Date>;
  last_delta_sync_at: Nullable<Date>;
  connected_at: Timestamp;
  disconnected_at: Nullable<Date>;
  connected_by_user_id: string | null;
  last_backfill_at: Nullable<Date>;
  last_backfill_stats: NullableJson<Record<string, number>>;
}

export interface ZohoPipelinesRow {
  workspace_id: Generated<string>;
  pipeline_id: string;
  label: string;
  stages: Json<ZohoPipelineStage[]>;
  updated_at: Generated<Date>;
}

export interface ZohoAccountsRow {
  workspace_id: Generated<string>;
  zoho_account_id: string;
  name: string | null;
  website: string | null;
  account_type: string | null;
  is_customer: boolean;
  employer_id: string | null;
  properties: Json<Record<string, unknown>>;
  account_owner: string | null;
  last_synced_at: Generated<Date>;
}

export interface CallLogsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  contact_id: string | null;
  contact_name: string;
  contact_phone: string | null;
  contact_email: string | null;
  direction: CallDirection;
  status: CallStatus;
  started_at: Generated<Date>;
  ended_at: Nullable<Date>;
  duration_sec: number | null;
  audio_storage_key: string | null;
  audio_content_type: Nullable<string>;
  audio_byte_size: number | null;
  transcript_text: string | null;
  summary: string | null;
  action_items: NullableJson<CallActionItem[]>;
  caller_user: string | null;
  error: string | null;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export type ProposedActionTrigger =
  | 'inbound_email'
  | 'forwarded_email'
  | 'email_opened'
  | 'cron_stage_stalled'
  | 'cron_deadline_approaching'
  | 'exception_detected'
  | 'tpa_requested'
  | 'voice_turn'
  | 'chat_turn'
  | 'insight';

export type ProposedActionType =
  | 'email_draft'
  | 'add_contact'
  | 'create_crm_contact'
  | 'apply_mutation'
  | 'create_exception'
  | 'ingest_attachment'
  | 'schedule_followup'
  | 'silent_file'
  | 'flag_triage'
  | 'save_memory'
  | 'link_participant'
  | 'attach_to_remediation'
  | 'create_ignore_rule';

export type ProposedActionStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'auto_executed'
  | 'expired'
  | 'superseded'
  | 'skipped'
  | 'no_action'
  | 'manual_waiver'
  | 'info_requested';

export interface ProposedActionsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string | null;
  trigger: ProposedActionTrigger;
  trigger_ref: string;
  type: ProposedActionType;
  status: ProposedActionStatus;
  payload: Json<unknown>;
  original_payload: ColumnType<unknown, string | null | undefined, string | null>;
  rejection_reason: string | null;
  parent_action_id: string | null;
  thread_id: string | null;
  source_message_id: string | null;
  summary: string | null;
  created_at: Timestamp;
  decided_at: Generated<Date | null>;
  decided_by_user_id: string | null;
  work_item_id: string | null;
}

export const AGENT_WAKEUP_KINDS = [
  'prepare_insight',
  'followup_check',
  'exception_check',
  'deadline_check',
] as const;
export type AgentWakeupKind = (typeof AGENT_WAKEUP_KINDS)[number];

export const AGENT_WAKEUP_STATUSES = [
  'scheduled',
  'processing',
  'completed',
  'cancelled',
  'failed',
] as const;
export type AgentWakeupStatus = (typeof AGENT_WAKEUP_STATUSES)[number];

export interface AgentWakeupPayloads {
  prepare_insight: { insight_id: string };
  followup_check: { proposed_action_id: string; reason: string };
  exception_check: { exception_id: string };
  deadline_check: { deadline_label: string; filing_deadline: string; bucket: number };
}
export type AgentWakeupPayload = AgentWakeupPayloads[AgentWakeupKind];

export interface AgentWakeupsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  // Nullable: a cross-plan or workspace-level wakeup has no single client.
  employer_id: string | null;
  kind: AgentWakeupKind;
  natural_key: string;
  due_at: Date;
  fingerprint: string | null;
  payload: Json<AgentWakeupPayload>;
  source_table: string | null;
  source_id: string | null;
  status: AgentWakeupStatus;
  lease_owner: Nullable<string>;
  lease_expires_at: Nullable<Date>;
  attempts: number;
  last_error: Nullable<string>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export const MATILDA_INSIGHT_KINDS = [
  'followup_due',
  'stage_stall',
  'deadline_approaching',
  'exception_open',
  'sponsor_wait',
  'stale_source',
  'cross_plan_failure',
] as const;
export type MatildaInsightKind = (typeof MATILDA_INSIGHT_KINDS)[number];

export const MATILDA_INSIGHT_STATUSES = ['open', 'resolved', 'expired', 'dismissed'] as const;
export type MatildaInsightStatus = (typeof MATILDA_INSIGHT_STATUSES)[number];

export const INSIGHT_LEVELS = ['low', 'medium', 'high'] as const;
export type InsightLevel = (typeof INSIGHT_LEVELS)[number];

// A pointer at the exact source record backing an insight claim — never copied
// content. The notification layer re-fetches through the recipient's own
// visibility before rendering anything.
export interface InsightEvidenceRef {
  table: string;
  id: string;
  note: string | null;
}

export interface MatildaInsightsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  // Nullable: a cross-plan pattern has no single client (mirrors proposed_actions).
  employer_id: string | null;
  kind: MatildaInsightKind;
  natural_key: string;
  title: string;
  body: string;
  evidence: Json<InsightEvidenceRef[]>;
  impact: InsightLevel;
  urgency: InsightLevel;
  novelty: InsightLevel;
  confidence: InsightLevel;
  fingerprint: string;
  owner_user_id: string | null;
  suggested_action: string | null;
  work_item_id: string | null;
  proposed_action_id: string | null;
  status: MatildaInsightStatus;
  expires_at: Nullable<Date>;
  resolved_at: Nullable<Date>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export const NOTIFICATION_STATUSES = [
  'queued',
  'sending',
  'sent',
  'delivered',
  'failed',
  'suppressed',
  'cancelled',
] as const;
export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number];

export const NOTIFICATION_DETAIL_LEVELS = ['minimal', 'detailed'] as const;
export type NotificationDetailLevel = (typeof NOTIFICATION_DETAIL_LEVELS)[number];

export interface UserNotificationPrefsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  user_id: string;
  email_enabled: boolean;
  timezone: string | null;
  quiet_hours_start: number | null;
  quiet_hours_end: number | null;
  weekends_ok: boolean;
  muted_categories: Json<MatildaInsightKind[]>;
  frequency_cap_days: number;
  surprises_enabled: boolean;
  detail_level: NotificationDetailLevel;
  paused_until: Nullable<Date>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export interface MatildaNotificationsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  insight_id: string;
  // The computed recipient. delivered_to_email records where the send actually
  // went — the global delivery override redirects every send while active.
  recipient_user_id: string;
  recipient_email: string;
  delivered_to_email: Nullable<string>;
  category: MatildaInsightKind;
  why_this_person: string;
  subject: string;
  idempotency_key: string;
  status: NotificationStatus;
  suppressed_reason: Nullable<string>;
  error: Nullable<string>;
  provider_message_id: Nullable<string>;
  provider_thread_id: Nullable<string>;
  insight_fingerprint: string;
  scheduled_for: Date;
  sent_at: Nullable<Date>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export interface MatildaNotificationRepliesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  notification_id: string;
  from_email: string;
  body_text: string;
  provider_message_id: string;
  received_at: Date;
  created_at: Timestamp;
}

export type MemoryScope = 'workspace' | 'client' | 'user';

export type MemoryKind = 'fact' | 'feedback';

export type MemorySource = 'user' | 'matilda';

export interface MemoriesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  scope: MemoryScope;
  kind: MemoryKind;
  employer_id: string | null;
  user_id: string | null;
  content: string;
  source: MemorySource;
  created_by_user_id: string | null;
  source_ref: string | null;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export type ChecklistSourceType = 'chat_message' | 'subagent_chat_message' | 'exception_remediation';

export interface RecommendationChecklistItemsRow {
  workspace_id: Generated<string>;
  source_type: ChecklistSourceType;
  source_id: string;
  item_index: number;
  checked: boolean;
  checked_by_user_id: string | null;
  checked_at: Generated<Date>;
}

// Source of truth for the work-item vocabularies. The value arrays live here in
// db/types because they back DB columns — deriving the route's zod enums from
// these means the wire contract can't drift from the schema, and changing a
// value forces a look here (and a migration). Migrations keep their own inline
// snapshots on purpose (a historical migration must not shift if a const here
// later changes).
export const WORK_ITEM_ORIGINS = [
  'email',
  'manual',
  'rk_portal',
  'schwab',
  'shuttle',
  'sponsor_portal',
  'participant_portal',
  'system',
  'phone',
] as const;
export type WorkItemOrigin = (typeof WORK_ITEM_ORIGINS)[number];

export const WORK_ITEM_REQUESTER_TYPES = [
  'participant',
  'sponsor_operator',
  'recordkeeper',
  'vendor',
  'kpc_internal',
  'system',
  'unknown',
] as const;
export type WorkItemRequesterType = (typeof WORK_ITEM_REQUESTER_TYPES)[number];

export const WORK_ITEM_STATES = [
  'new',
  'triage',
  'needs_identity_confirmation',
  'needs_info',
  'proposed_action',
  'awaiting_approval',
  'manual_action_required',
  'waiting_external',
  'completed',
  'denied',
  'canceled',
  'escalated',
] as const;
export type WorkItemState = (typeof WORK_ITEM_STATES)[number];

export const WORK_ITEM_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type WorkItemPriority = (typeof WORK_ITEM_PRIORITIES)[number];

export const WORK_ITEM_LINK_TYPES = [
  'email_thread',
  'email_message',
  'email_attachment',
  'event',
  'shuttle_batch',
  'payroll_exception',
  'payroll_remediation',
  'participant',
  'employer',
  'plan_rules_version',
  'claim',
  'notice',
  'distribution',
  'proof_artifact',
  'participant_election',
  'election_verification',
  'claim_appeal',
  'notice_obligation',
  'notice_template',
  'pay_run',
  'census_change_set',
] as const;
export type WorkItemLinkType = (typeof WORK_ITEM_LINK_TYPES)[number];

// Low confidence routes to triage and cannot drive automated execution.
export const WORK_ITEM_CONFIDENCE_BUCKETS = ['high', 'medium', 'low'] as const;
export type WorkItemConfidenceBucket = (typeof WORK_ITEM_CONFIDENCE_BUCKETS)[number];

export const WORK_ITEM_CLOSURE_REASONS = [
  'approved_action',
  'reply_only',
  'no_action',
  'duplicate',
  'reclassified',
  'manual_resolved',
] as const;
export type WorkItemClosureReason = (typeof WORK_ITEM_CLOSURE_REASONS)[number];

// Producer-written launch-control blockers on a work item (COG-290). Each
// producing domain appends its own; the approval gate refuses approval while
// any are open and renders {message, nextAction} on the Today card.
export interface Blocker {
  code: string;
  message: string;
  nextAction: string;
}

export interface WorkItemsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  // Nullable: a participant request can exist before its plan is known. Workspace
  // RLS still isolates it; the sponsor RESTRICTIVE policy excludes employer-null rows.
  employer_id: string | null;
  participant_id: string | null;
  origin: WorkItemOrigin;
  requester_type: WorkItemRequesterType;
  work_type: string;
  state: WorkItemState;
  parent_work_item_id: string | null;
  assignee_user_id: string | null;
  priority: WorkItemPriority;
  due_date: Generated<Date | null>;
  fiduciary_flag: boolean;
  escalation_flag: boolean;
  identity_confirmed: boolean;
  summary: string | null;
  completion_note: string | null;
  dedupe_key: string | null;
  requester_name: string | null;
  requester_email: string | null;
  requester_phone: string | null;
  classification_confidence: WorkItemConfidenceBucket | null;
  closure_reason: WorkItemClosureReason | null;
  last_activity_at: Generated<Date>;
  created_at: Timestamp;
  closed_at: Generated<Date | null>;
  blockers: Json<Blocker[]>;
  // Whether this item may surface as its own Today card. Email-origin items set
  // it from the classifier's "RM needs to act today" verdict; other origins
  // default true.
  today_eligible: boolean;
}

export interface WorkItemLinksRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  work_item_id: string;
  link_type: WorkItemLinkType;
  ref_id: string;
  created_at: Timestamp;
}

export interface KeyParticipantsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  // Nullable: a key participant (owner/officer) may not be on the payroll
  // roster, or may simply not be linked yet. Unlinked rows surface as a
  // today-screen item to prompt the manual link.
  participant_id: string | null;
  full_name: string;
  is_officer: boolean;
  ownership_percent: number | null;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

// Event-log value types live here, in external/db, so the DB row types never
// depend on a domain module (a domain type edit must not implicitly change the
// schema types). Domain code imports these from '@/external/db/types'.
export type AttributeKey =
  | 'sponsor_id'
  | 'participant_id'
  | 'plan_year_id'
  | 'trace_id'
  | 'email_id'
  | 'remediation_id'
  | 'employer_id'
  | 'work_item_id'
  | 'proposed_action_id'
  | 'sent_message_id'
  | 'approver_role'
  | 'approver_authority'
  | 'approval_decision'
  | 'blocker_code'
  | 'email_thread_id'
  | 'email_message_id'
  | 'email_attachment_id'
  | 'work_type'
  | 'confidence'
  | 'route_target'
  | 'closure_reason'
  | 'dwloan_request_id'
  | 'participant_election_id'
  | 'election_verification_id'
  | 'claim_id'
  | 'claim_appeal_id'
  | 'notice_obligation_id'
  | 'notice_type'
  | 'template_version'
  | 'delivery_channel'
  | 'insight_id'
  | 'notification_id'
  | 'notification_status'
  | 'recipient_user_id'
  | 'totp_connection_id'
  | 'totp_source';

export interface EventMessage {
  title: string;
  content: string;
}

export type EventAttributes = Partial<Record<AttributeKey, string>>;

export interface EventLogRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  occurred_at: Timestamp;
  actor_user_id: string | null;
  event_name: string;
  event_messages: Json<EventMessage[]>;
  event_attributes: Json<EventAttributes>;
}

// ---------------------------------------------------------------------------
// COG-312 — Payroll remediation loop + 3(16) escalation
// ---------------------------------------------------------------------------

export type PayrollRemediationState =
  | 'detected'
  | 'needs_owner_assignment'
  | 'awaiting_sponsor'
  | 'awaiting_rk'
  | 'awaiting_kpc'
  | 'awaiting_corrected_data'
  | 'ready_to_recheck'
  | 'resolved'
  | 'escalated'
  | 'waived_manual';

export type PayrollRemediationIssueType =
  | 'missing_employee'
  | 'demographic_mismatch'
  | 'duplicate_employee'
  | 'missing_compensation'
  | 'early_or_ineligible_contribution'
  | 'incorrect_deferral'
  | 'incorrect_match'
  | 'source_misclassification'
  | 'eligibility_edge_case'
  | 'plan_code_mismatch'
  | 'stale_or_missing_feed'
  | 'late_remittance'
  | 'limit_issue'
  | 'loan_repayment_issue'
  | 'other';

export type PayrollRemediationResponsibleParty =
  | 'sponsor'
  | 'kpc'
  | 'recordkeeper'
  | 'cognitory'
  | 'unknown';

export type PayrollRemediationRecheckStatus = 'passed' | 'failed';

export interface PayrollRemediationItemsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  dedup_key: string;
  issue_type: PayrollRemediationIssueType;
  severity: ExceptionSeverity;
  state: PayrollRemediationState;
  responsible_party: PayrollRemediationResponsibleParty | null;
  owner_user_id: string | null;
  is_316_sensitive: boolean;
  due_date: DateString | null;
  source_pay_period_id: string | null;
  source_ref: NullableJson<Record<string, unknown>>;
  affected_participant_ids: Json<string[]>;
  plan_rules_version: string | null;
  rule_citation: string | null;
  suggested_remediation: NullableJson<Remediation>;
  evidence: NullableJson<ExceptionRawData>;
  summary: string;
  next_action: string | null;
  recheck_status: PayrollRemediationRecheckStatus | null;
  proof: NullableJson<Record<string, unknown>>;
  waiver_reason: string | null;
  closed_at: Generated<Date | null>;
  closed_by_user_id: string | null;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

// User-defined materiality suppression (migration 114). employer_id null ⟹
// workspace-wide; issue_type null ⟹ every issue type; ignored_through null ⟹
// never expires.
export interface PayrollIgnoreRulesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string | null;
  issue_type: PayrollRemediationIssueType | null;
  max_variance_amount: number;
  reason: string;
  ignored_through: Generated<string | null>;
  created_by_user_id: string | null;
  created_at: Timestamp;
}

export interface TodayDismissalsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  origin_type: TodayOriginType;
  origin_id: string;
  dismissed_by_user_id: string;
  reason: string | null;
  created_at: Timestamp;
}

export type FeatureFlagId =
  | 'kpc_cobranding'
  | 'partners'
  | 'kpc_branding'
  | 'schwab_payroll_auto_launch'
  | 'schwab_payroll_halt'
  | 'srt_setup_halt'
  | 'srt_setup_paused'
  | 'hide_unavailable';

export type WorkspaceFeatureFlags = Record<FeatureFlagId, boolean>;

export interface UserPreferences {
  auto_confirm: boolean;
  show_tool_logs: boolean;
  tour_completed: boolean;
  tour_dismissed_steps: string[];
}

// One row per workspace / per user; the jsonb holds only the keys that diverge
// from the code-computed defaults, so the full config is {...defaults, ...stored}.
export interface WorkspaceFeatureFlagsRow {
  workspace_id: Generated<string>;
  flags: Json<Partial<WorkspaceFeatureFlags>>;
  updated_at: Generated<Date>;
}

export interface UserPreferencesRow {
  workspace_id: Generated<string>;
  user_id: string;
  prefs: Json<Partial<UserPreferences>>;
  updated_at: Generated<Date>;
}

export interface TodayCardSeqRow {
  workspace_id: Generated<string>;
  item_key: string;
  seq: number;
  assigned_at: Generated<Timestamp>;
  ai_title: string | null;
  ai_subtitle: string | null;
}

export interface TodayCardSeqCounterRow {
  workspace_id: Generated<string>;
  next_seq: number;
}

export const DWLOAN_REQUEST_KINDS = [
  'termination_distribution',
  'hardship',
  'in_service',
  'rollover',
  'loan',
  'qdro',
  'death',
  'rmd',
  'other_manual',
] as const;
export type DwloanRequestKind = (typeof DWLOAN_REQUEST_KINDS)[number];

// The request-specific lifecycle; work_items.state stays the coarse Today state.
export const DWLOAN_SUBSTATES = [
  'request_received',
  'identity_confirmation_needed',
  'triage',
  'missing_info',
  'plan_rule_review',
  'sponsor_approval_needed',
  'ready_for_rm_approval',
  'approved_for_reply',
  'approved_for_handoff',
  'waiting_participant',
  'waiting_sponsor',
  'waiting_rk',
  'completed_with_proof',
  'denied',
  'withdrawn',
  'no_action',
  'manual_exception',
] as const;
export type DwloanSubstate = (typeof DWLOAN_SUBSTATES)[number];

export const DWLOAN_MATCH_CONFIDENCES = ['high', 'medium', 'low', 'unmatched'] as const;
export type DwloanMatchConfidence = (typeof DWLOAN_MATCH_CONFIDENCES)[number];

export const DWLOAN_PROPOSED_NEXT_STEPS = [
  'missing_info_reply',
  'sponsor_confirmation',
  'manual_review',
  'rk_handoff',
  'denial',
  'withdrawal',
  'no_action',
  'escalation',
] as const;
export type DwloanProposedNextStep = (typeof DWLOAN_PROPOSED_NEXT_STEPS)[number];

export const DWLOAN_WAITING_PARTIES = ['participant', 'sponsor', 'recordkeeper', 'kpc', 'none'] as const;
export type DwloanWaitingParty = (typeof DWLOAN_WAITING_PARTIES)[number];

// `citation` is null when the provision is named but unsourced.
export interface DwloanPlanRuleCitation {
  provision: string;
  citation: string | null;
}

// Snapshot taken at RM approval so the audit trail reflects what was handed off.
export interface DwloanHandoffPacket {
  evidence: string[];
  known_facts: string[];
  missing_assumptions: string[];
  rk_instructions: string;
  assembled_at: string;
}

// A pointer to a stored artifact rides the work_item_links `proof_artifact` type, not here.
export interface DwloanProof {
  kind: string;
  note: string | null;
}

export interface DwloanRequestDetails {
  requested_amount: number | null;
  loan_purpose: string | null;
  term_months: number | null;
  // The §1.401(k)-1(d)(3) safe-harbor need the participant cited, when the request is a hardship; the RM's
  // substantiation form seeds its event from this so the classifier's read isn't re-keyed by hand.
  safe_harbor_event: HardshipSafeHarborEvent | null;
}

// The deemed immediate-and-heavy financial needs of Treas. Reg. §1.401(k)-1(d)(3)(ii).
export const HARDSHIP_SAFE_HARBOR_EVENTS = [
  'medical_care',
  'principal_residence_purchase',
  'post_secondary_education',
  'prevent_eviction_or_foreclosure',
  'funeral_expenses',
  'casualty_repair',
  'federal_disaster',
] as const;
export type HardshipSafeHarborEvent = (typeof HARDSHIP_SAFE_HARBOR_EVENTS)[number];

export const HARDSHIP_CERTIFICATION_MODES = ['self_certified', 'documented'] as const;
export type HardshipCertificationMode = (typeof HARDSHIP_CERTIFICATION_MODES)[number];

export const HARDSHIP_SUBSTANTIATION_OUTCOMES = ['passed', 'failed', 'overridden'] as const;
export type HardshipSubstantiationOutcome = (typeof HARDSHIP_SUBSTANTIATION_OUTCOMES)[number];

// RM-entered per-source balances (not in our data model), each traced to recordkeeper evidence.
export interface HardshipSourceAvailability {
  source: MoneySource;
  available_amount: number;
  evidence_ref: string;
}

// The participant's written SECURE 2.0 certification — covering the event, the amount, and the lack of
// reasonably available alternative means.
export interface HardshipParticipantCertification {
  evidence_ref: string;
  certified_by: string;
  certified_at: string;
}

export const HARDSHIP_CRITERION_KEYS = [
  'plan_allows_hardship',
  'safe_harbor_event_selected',
  'need_documented',
  'amount_within_need_plus_taxes',
  'other_distributions_taken',
  'insufficient_resources_represented',
  'source_availability_sufficient',
] as const;
export type HardshipCriterionKey = (typeof HARDSHIP_CRITERION_KEYS)[number];

export interface HardshipCriterion {
  key: HardshipCriterionKey;
  label: string;
  passed: boolean;
  detail: string | null;
}

// Evaluated at record time; the gate reads only `outcome` — the rest is the audit record.
export interface HardshipSubstantiation {
  safe_harbor_event: HardshipSafeHarborEvent | null;
  certification_mode: HardshipCertificationMode;
  documented_need_amount: number | null;
  tax_and_penalty_amount: number | null;
  requested_amount: number | null;
  max_permitted_amount: number | null;
  source_availability: HardshipSourceAvailability[];
  evidence_refs: string[];
  participant_certification: HardshipParticipantCertification | null;
  criteria: HardshipCriterion[];
  outcome: HardshipSubstantiationOutcome;
  override: { reason: string; recorded_at: string } | null;
  evaluated_at: string;
}

export interface DwloanRequestsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  participant_id: string | null;
  work_item_id: string;
  request_kind: DwloanRequestKind;
  substate: DwloanSubstate;
  match_confidence: DwloanMatchConfidence;
  employment_status_context: string | null;
  proposed_next_step: DwloanProposedNextStep | null;
  proposed_reasoning: string | null;
  missing_info: Json<string[]>;
  sponsor_approval_required: boolean;
  notices_forms_dependencies: Json<string[]>;
  plan_rule_citations: Json<DwloanPlanRuleCitation[]>;
  waiting_party: DwloanWaitingParty | null;
  handoff_packet: NullableJson<DwloanHandoffPacket>;
  proof: NullableJson<DwloanProof>;
  request_details: NullableJson<DwloanRequestDetails>;
  hardship_substantiation: NullableJson<HardshipSubstantiation>;
  closure_note: string | null;
  rm_approved_by_user_id: string | null;
  rm_approved_at: Generated<Date | null>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
  closed_at: Generated<Date | null>;
}

export const PARTICIPANT_ELECTION_KINDS = [
  'start_deferral',
  'change_deferral_rate',
  'stop_deferral',
  'roth_pretax_split',
  'auto_enroll_default',
  'opt_out',
  'opt_out_with_refund',
] as const;
export type ParticipantElectionKind = (typeof PARTICIPANT_ELECTION_KINDS)[number];

export const PARTICIPANT_ELECTION_SUBSTATES = [
  'request_received',
  'identity_confirmation_needed',
  'triage',
  'missing_info',
  'plan_rule_review',
  'notice_dependency_check',
  'ready_for_rm_approval',
  'approved_for_action',
  'waiting_external',
  'completed_with_proof',
  'withdrawn',
  'no_action',
  'manual_exception',
] as const;
export type ParticipantElectionSubstate = (typeof PARTICIPANT_ELECTION_SUBSTATES)[number];

export const ELECTION_MATCH_CONFIDENCES = ['high', 'medium', 'low', 'unmatched'] as const;
export type ElectionMatchConfidence = (typeof ELECTION_MATCH_CONFIDENCES)[number];

// Refund status, tracked separately from future-withholding verification.
export const ELECTION_REFUND_STATUSES = ['not_applicable', 'pending', 'requested', 'completed'] as const;
export type ElectionRefundStatus = (typeof ELECTION_REFUND_STATUSES)[number];

// Which fields apply depends on the election kind (a stop has no rate; a Roth split has both pcts).
export interface ElectionStateSnapshot {
  pretax_pct: number | null;
  roth_pct: number | null;
  amount: number | null;
  rate: number | null;
  note: string | null;
}

export interface ElectionPlanRuleCitation {
  provision: string;
  citation: string | null;
}

export interface ElectionProof {
  kind: string;
  note: string | null;
}

// Ids and a short label only, never PII.
export interface ElectionSourceRef {
  origin: 'email' | 'manual' | 'auto_enroll' | 'eligibility' | 'payroll_mismatch' | 'rk' | 'sponsor';
  thread_id: string | null;
  work_item_id: string | null;
  label: string | null;
}

export interface ParticipantElectionsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  participant_id: string | null;
  work_item_id: string;
  election_kind: ParticipantElectionKind;
  substate: ParticipantElectionSubstate;
  match_confidence: ElectionMatchConfidence;
  current_state: NullableJson<ElectionStateSnapshot>;
  requested_state: NullableJson<ElectionStateSnapshot>;
  effective_pay_date: DateString | null;
  refund_status: ElectionRefundStatus | null;
  proposed_reasoning: string | null;
  missing_info: Json<string[]>;
  notices_forms_dependencies: Json<string[]>;
  plan_rule_citations: Json<ElectionPlanRuleCitation[]>;
  source_ref: NullableJson<ElectionSourceRef>;
  proof: NullableJson<ElectionProof>;
  closure_note: string | null;
  rm_approved_by_user_id: string | null;
  rm_approved_at: Generated<Date | null>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
  closed_at: Generated<Date | null>;
}

export const ELECTION_VERIFICATION_STATUSES = ['pending', 'passed', 'failed', 'missing'] as const;
export type ElectionVerificationStatus = (typeof ELECTION_VERIFICATION_STATUSES)[number];

export interface ElectionVerificationEvidence {
  pay_run_ids: string[];
  observed_pretax: number | null;
  observed_roth: number | null;
  note: string | null;
}

export interface ParticipantElectionVerificationsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  participant_id: string;
  participant_election_id: string;
  work_item_id: string;
  parent_work_item_id: string;
  cycle_pay_date: DateString;
  cycle_index: number;
  expected_pretax: number | null;
  expected_roth: number | null;
  expected_rate: number | null;
  observed_pretax: number | null;
  observed_roth: number | null;
  status: ElectionVerificationStatus;
  evidence: NullableJson<ElectionVerificationEvidence>;
  remediation_item_id: string | null;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export type NoticeObligationTriggerKind = 'date_based' | 'event_triggered';

// The coverage registry classifies each notice type; an obligation can override.
export const NOTICE_OWNER_CLASSIFICATIONS = [
  'kpc',
  'rk',
  'vendor',
  'ftwilliam',
  'sponsor',
  'manual',
] as const;
export type NoticeOwnerClassification = (typeof NOTICE_OWNER_CLASSIFICATIONS)[number];

export const NOTICE_DELIVERY_CHANNEL_CLASSES = [
  'ftwilliam',
  'rk',
  'sponsor',
  'email',
  'paper',
  'undecided',
] as const;
export type NoticeDeliveryChannelClass = (typeof NOTICE_DELIVERY_CHANNEL_CLASSES)[number];

export const NOTICE_COVERAGE_STATUSES = [
  'covered',
  'tracked_manual',
  'vendor_owned',
  'excluded',
  'undecided',
] as const;
export type NoticeCoverageStatus = (typeof NOTICE_COVERAGE_STATUSES)[number];

export const NOTICE_OBLIGATION_SUBSTATES = [
  'created',
  'draft_generated',
  'packet_ready',
  'rm_approved',
  'handoff_recorded',
  'evidence_attached',
  'blocked',
  'skipped',
  'closed',
] as const;
export type NoticeObligationSubstate = (typeof NOTICE_OBLIGATION_SUBSTATES)[number];

// A blocker that holds the obligation out of approval.
export const NOTICE_BLOCKER_KINDS = [
  'missing_contact',
  'missing_source',
  'missing_owner_or_channel',
  'missing_eligibility',
  'no_recipients',
] as const;
export type NoticeBlockerKind = (typeof NOTICE_BLOCKER_KINDS)[number];

// Ids and a label only, never PII.
export interface NoticeSourceRef {
  kind: NoticeObligationTriggerKind;
  rule: string | null;
  event_ref: string | null;
  label: string | null;
}

// Snapshot assembled at RM approval — what would be handed off, never sent from here.
export interface NoticeDeliveryReadyPacket {
  pdf_preview_key: string | null;
  email_subject: string | null;
  email_body: string | null;
  recipient_readiness: { ready: number; blocked: number; total: number };
  delivery_channel: NoticeDeliveryChannelClass;
  source_evidence: string[];
  plan_rule_citations: string[];
  template_version: number | null;
  open_blockers: string[];
  assembled_at: string;
}

export interface NoticeOutsideDeliveryEvidence {
  handled_by: NoticeOwnerClassification;
  kind: string;
  note: string | null;
}

export interface NoticeObligationsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  plan_year_id: string | null;
  notice_type: NoticeType;
  trigger_kind: NoticeObligationTriggerKind;
  source_ref: NullableJson<NoticeSourceRef>;
  window_open: DateString | null;
  due_date: DateString | null;
  recipient_scope: Json<NoticeRecipientFilter>;
  owner_classification: NoticeOwnerClassification;
  delivery_channel_classification: NoticeDeliveryChannelClass;
  coverage_status: NoticeCoverageStatus;
  template_version: number | null;
  substate: NoticeObligationSubstate;
  blockers: Json<NoticeBlockerKind[]>;
  work_item_id: string;
  packet: NullableJson<NoticeDeliveryReadyPacket>;
  outside_delivery_evidence: NullableJson<NoticeOutsideDeliveryEvidence>;
  skip_reason: string | null;
  dedupe_key: string;
  rm_approved_by_user_id: string | null;
  rm_approved_at: Generated<Date | null>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
  closed_at: Generated<Date | null>;
}

export const NOTICE_TEMPLATE_STATUSES = ['active', 'superseded', 'draft'] as const;
export type NoticeTemplateStatus = (typeof NOTICE_TEMPLATE_STATUSES)[number];

export interface NoticeTemplatesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  notice_type: NoticeType;
  version: number;
  status: NoticeTemplateStatus;
  source: string;
  subject: string;
  body: string;
  required_merge_fields: Json<string[]>;
  approved_by_user_id: string | null;
  approved_at: Generated<Date | null>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export interface Database {
  workspaces: WorkspacesRow;
  users: UsersRow;
  event_log: EventLogRow;
  proposed_actions: ProposedActionsRow;
  agent_wakeups: AgentWakeupsRow;
  matilda_insights: MatildaInsightsRow;
  user_notification_prefs: UserNotificationPrefsRow;
  matilda_notifications: MatildaNotificationsRow;
  matilda_notification_replies: MatildaNotificationRepliesRow;
  work_items: WorkItemsRow;
  key_participants: KeyParticipantsRow;
  work_item_links: WorkItemLinksRow;
  dwloan_requests: DwloanRequestsRow;
  participant_elections: ParticipantElectionsRow;
  participant_election_verifications: ParticipantElectionVerificationsRow;
  notice_obligations: NoticeObligationsRow;
  notice_templates: NoticeTemplatesRow;
  memories: MemoriesRow;
  employers: EmployersRow;
  plan_years: PlanYearsRow;
  uploads: UploadsRow;
  participants: ParticipantsRow;
  participant_aliases: ParticipantAliasesRow;
  shuttle_plan_employers: ShuttlePlanEmployersRow;
  shuttle_ignored_plan_codes: ShuttleIgnoredPlanCodesRow;
  shuttle_exports: ShuttleExportsRow;
  shuttle_employer_sync_receipts: ShuttleEmployerSyncReceiptsRow;
  plan_documents: PlanDocumentsRow;
  pre_approved_plan_document_extractions: PreApprovedPlanDocumentExtractionsRow;
  ingest_batches: IngestBatchesRow;
  payroll_console_pulls: PayrollConsolePullsRow;
  totp_connections: TotpConnectionsRow;
  extraction_mappings: ExtractionMappingsRow;
  census_snapshots: CensusSnapshotsRow;
  census_rows: CensusRowsRow;
  census_change_sets: CensusChangeSetsRow;
  census_change_items: CensusChangeItemsRow;
  census_change_approvals: CensusChangeApprovalsRow;
  census_changes: CensusChangesRow;
  pay_runs: PayRunsRow;
  pay_run_lines: PayRunLinesRow;
  payroll_cycle_runs: PayrollCycleRunsRow;
  schwab_payroll_submissions: SchwabPayrollSubmissionsRow;
  schwab_api_calls: SchwabApiCallsRow;
  schwab_api_credentials: SchwabApiCredentialsRow;
  srt_setup_runs: SrtSetupRunsRow;
  srt_setup_run_plans: SrtSetupRunPlansRow;
  srt_portal_credentials: SrtPortalCredentialsRow;
  schwab_plan_setup: SchwabPlanSetupRow;
  rk_extracts: RkExtractsRow;
  rk_deposits: RkDepositsRow;
  rk_balance_observations: RkBalanceObservationsRow;
  pipeline_runs: PipelineRunsRow;
  pipeline_run_payroll_batches: PipelineRunPayrollBatchesRow;
  participant_computations: ParticipantComputationsRow;
  distributions: DistributionsRow;
  loans: LoansRow;
  trust_rollforward: TrustRollforwardRow;
  test_results: TestResultsRow;
  exceptions: ExceptionsRow;
  corrections: CorrectionsRow;
  filings: FilingsRow;
  ftw_sessions: FtwSessionsRow;
  ftw_action_log: FtwActionLogRow;
  ftw_credentials: FtwCredentialsRow;
  ftw_shared_credentials: FtwSharedCredentialsRow;
  ftw_link_credentials: FtwLinkCredentialsRow;
  docusign_credentials: DocusignCredentialsRow;
  schwab_forms: SchwabFormsRow;
  conversion_signers: ConversionSignersRow;
  chat_messages: ChatMessagesRow;
  api_calls: ApiCallsRow;
  subagents: SubagentsRow;
  subagent_employers: SubagentEmployersRow;
  subagent_chat_messages: SubagentChatMessagesRow;
  global_chat_messages: GlobalChatMessagesRow;
  matilda_action_log: MatildaActionLogRow;
  email_threads: EmailThreadsRow;
  email_thread_read_state: EmailThreadReadStateRow;
  email_messages: EmailMessagesRow;
  email_attachments: EmailAttachmentsRow;
  ignored_email_senders: IgnoredEmailSendersRow;
  onboarding_sessions: OnboardingSessionsRow;
  onboarding_chat_messages: OnboardingChatMessagesRow;
  plan_conversions: PlanConversionsRow;
  onboarding_envelopes: OnboardingEnvelopesRow;
  onboarding_adoption_agreements: OnboardingAdoptionAgreementsRow;
  phone_contacts: PhoneContactsRow;
  crm_client_meta: CrmClientMetaRow;
  crm_contacts: CrmContactsRow;
  crm_projects: CrmProjectsRow;
  call_logs: CallLogsRow;
  hubspot_credentials: HubspotCredentialsRow;
  hubspot_pipelines: HubspotPipelinesRow;
  hubspot_companies: HubspotCompaniesRow;
  zoho_credentials: ZohoCredentialsRow;
  zoho_pipelines: ZohoPipelinesRow;
  zoho_accounts: ZohoAccountsRow;
  today_fact_snapshots: TodayFactSnapshotsRow;
  browser_computer_use_platforms: BrowserComputerUsePlatformsRow;
  browser_computer_use_tasks: BrowserComputerUseTasksRow;
  browser_computer_use_sessions: BrowserComputerUseSessionsRow;
  browser_computer_use_action_log: BrowserComputerUseActionLogRow;
  browser_computer_use_downloads: BrowserComputerUseDownloadsRow;
  payroll_connection_infos: PayrollConnectionInfosRow;
  payroll_console_credentials: PayrollConsoleCredentialsRow;
  payroll_fetch_runs: PayrollFetchRunsRow;
  recordkeeper_connection_infos: RecordkeeperConnectionInfosRow;
  recordkeeper_fetch_runs: RecordkeeperFetchRunsRow;
  workspace_invites: WorkspaceInvitesRow;
  gmail_connections: GmailConnectionsRow;
  regulations: RegulationsRow;
  regulation_chunks: RegulationChunksRow;
  w2_records: W2RecordsRow;
  recommendation_checklist_items: RecommendationChecklistItemsRow;
  workspace_3_16_config: Workspace316ConfigRow;
  notices: NoticesRow;
  deliveries: DeliveriesRow;
  delivery_events: DeliveryEventsRow;
  claims: ClaimsRow;
  claim_appeals: ClaimAppealsRow;
  rts_searches: RtsSearchesRow;
  participant_otps: ParticipantOtpsRow;
  participant_identify_attempts: ParticipantIdentifyAttemptsRow;
  back_office_admins: BackOfficeAdminsRow;
  back_office_sql_queries: BackOfficeSqlQueriesRow;
  workspace_mailboxes: WorkspaceMailboxesRow;
  email_message_receipts: EmailMessageReceiptsRow;
  payroll_remediation_items: PayrollRemediationItemsRow;
  payroll_ignore_rules: PayrollIgnoreRulesRow;
  today_dismissals: TodayDismissalsRow;
  workspace_feature_flags: WorkspaceFeatureFlagsRow;
  user_preferences: UserPreferencesRow;
  today_card_seq: TodayCardSeqRow;
  today_card_seq_counter: TodayCardSeqCounterRow;
  today_screen_items: TodayScreenItemsView;
}

export type BcuSessionStatus = 'running' | 'completed' | 'failed' | 'aborted';
export type BcuActionType = 'click' | 'set_field' | 'key' | 'screenshot' | 'scroll' | 'wait';

export interface BrowserComputerUsePlatformsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  name: string;
  description: string | null;
  start_url: string;
  system_prompt: string;
  storage_state: string | null;
  storage_state_updated_at: Nullable<Date>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export interface BrowserComputerUseTasksRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  platform_id: string;
  name: string;
  description: string | null;
  prompt: string;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export interface BrowserComputerUseSessionsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  platform_id: string;
  task_id: string | null;
  status: BcuSessionStatus;
  started_at: Timestamp;
  ended_at: Nullable<Date>;
  output_text: string | null;
  failure_reason: string | null;
  recording_url: string | null;
}

export interface BrowserComputerUseActionLogRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  session_id: string;
  step_number: number;
  occurred_at: Timestamp;
  action_type: BcuActionType;
  page_url: string;
  payload: ColumnType<unknown, string | null, string | null>;
  screenshot_url: string | null;
}

export interface BrowserComputerUseDownloadsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  session_id: string;
  captured_at: Timestamp;
  suggested_filename: string;
  source_url: string;
  page_url: string;
  storage_key: string;
  byte_size: number;
}

export interface ApiCallsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  task: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  cost_usd: ColumnType<number, number | string, number | string>;
  created_at: Timestamp;
}

export interface ChatMessagesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  stage_slug: string;
  role: ColumnType<'user' | 'agent', 'user' | 'agent', 'user' | 'agent'>;
  agent: string | null;
  body: string;
  attach: NullableJson<{ header: string; rows: [string, string][] }>;
  actions: ColumnType<unknown, string | null, string | null>;
  // bigserial — tiebreaker for ordering when two rows share a created_at us.
  seq: Generated<number>;
  created_at: Timestamp;
}

export type NewPreApprovedPlanDocumentExtraction = KyselyInsertable<PreApprovedPlanDocumentExtractionsRow>;

export interface MatildaActionLogRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string | null;
  message_id: string | null;
  tool_name: string;
  risk: MatildaActionRisk;
  status: MatildaActionStatus;
  args: Json<unknown>;
  result: ColumnType<unknown, string | null, string | null>;
  error: string | null;
  started_at: Generated<Date>;
  completed_at: ColumnType<Date | null, Date | null | undefined, Date | null | undefined>;
  tool_use_id: string | null;
  summary: string | null;
  stage_slug: string | null;
  assistant_blocks: ColumnType<unknown, string | null, string | null>;
}

// Null = not dispositioned; the thread's Inbox↔Today state then derives from linked work_items.
export type ThreadWorkDisposition = 'reply_only' | 'no_action';

export interface EmailThreadsRow {
  id: Generated<string>;
  workspace_id: ColumnType<string, never, never>;
  employer_id: string | null;
  original_inbox_owner_user_id: string | null;
  gmail_thread_id: Nullable<string>;
  subject: string;
  participant_emails: Json<string[]>;
  last_message_at: Generated<Date>;
  work_disposition: ThreadWorkDisposition | null;
  mailbox_id: Generated<string | null>;
  // The mailbox owner approved the matched client's Lead Consultant to see/action
  // this thread (only meaningful for personal-mailbox threads whose LC isn't the
  // owner). Shared-inbox threads (owner NULL) are visible to the LC regardless.
  shared_with_lc: boolean;
  archived_at: Generated<Date | null>;
  snoozed_until: Generated<Date | null>;
  created_at: Timestamp;
}

export interface EmailThreadReadStateRow {
  user_id: string;
  thread_id: string;
  workspace_id: Generated<string>;
  last_read_at: Generated<Date>;
}

export type EmailRoutingStatus = 'routed' | 'triage';

export type EmailSenderKind = 'participant' | 'client' | 'advisor' | 'recordkeeper' | 'payroll' | 'unknown';
export type EmailTopic =
  | 'contribution_change'
  | 'distribution'
  | 'loan'
  | 'hardship'
  | 'eligibility'
  | 'plan_design'
  | 'census_payroll'
  | 'billing'
  | 'document_request'
  | 'escalation'
  | 'calendar_scheduling'
  | 'not_plan_related'
  | 'spam';
export type EmailUrgency = 'time_sensitive' | 'normal';

export interface EmailClassification {
  sender_kind: EmailSenderKind;
  topic: EmailTopic;
  urgency: EmailUrgency;
  matched_employer_id: string | null;
  confidence: number;
  rationale: string;
}

export interface EmailMessagesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  thread_id: string;
  employer_id: string | null;
  direction: EmailDirection;
  message_id_header: string | null;
  in_reply_to_header: string | null;
  references_headers: NullableJson<string[]>;
  from_email: string;
  from_name: string | null;
  to_emails: Json<string[]>;
  cc_emails: NullableJson<string[]>;
  subject: string;
  body_text: string | null;
  body_html: string | null;
  snippet: string | null;
  error: string | null;
  matilda_action_summary: string | null;
  routing_status: EmailRoutingStatus;
  triage_reason: string | null;
  forwarded_from_user_id: string | null;
  classification: NullableJson<EmailClassification>;
  gmail_message_id: Nullable<string>;
  created_at: Timestamp;
}

export interface EmailAttachmentsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  message_id: string | null;
  employer_id: string | null;
  filename: string;
  content_type: string;
  storage_key: string;
  byte_size: number;
  disposition: EmailAttachmentDisposition;
  upload_id: string | null;
  classified_as: string | null;
  error: string | null;
  created_at: Timestamp;
}

export type WorkspaceMailboxKind = 'shared' | 'personal';
export type WorkspaceMailboxStatus = 'active' | 'paused' | 'error';

export interface WorkspaceMailboxesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  provider: 'gmail';
  address: string;
  display_name: string | null;
  kind: WorkspaceMailboxKind;
  owner_user_id: string | null;
  gmail_history_id: string | null;
  watch_expiration: Nullable<Date>;
  status: WorkspaceMailboxStatus;
  created_at: Timestamp;
}

export interface EmailMessageReceiptsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  message_id: string;
  mailbox_id: string;
  gmail_message_id: string;
  gmail_thread_id: string;
  created_at: Timestamp;
}

export interface OnboardingSessionsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  client_name: string;
  client_email: string | null;
  status: OnboardingSessionStatus;
  recording_path: string | null;
  recording_duration_sec: number | null;
  transcript: string | null;
  plan_features: NullableJson<PlanFeatures>;
  plan_document_text: string | null;
  client_token: string | null;
  client_signed_at: Nullable<Date>;
  client_signature_name: string | null;
  client_signature_ip: string | null;
  employer_id: string | null;
  error_message: string | null;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export interface OnboardingChatMessagesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  session_id: string;
  role: OnboardingChatRole;
  body: string;
  created_at: Timestamp;
}

// Existing-(k)PC-client conversion lifecycle. `stage = 'live'` is the
// authoritative signal that the plan is fully converted and we act as its
// administrator of record; everything before that is preparatory.
export type PlanConversionStage =
  | 'creating'
  | 'awaiting_signatures'
  | 'needs_signer'
  | 'signed'
  | 'schwab_load'
  | 'live'
  | 'failed';

// The DocuSign envelope statuses we persist from the Connect webhook.
export type DocusignEnvelopeStatus = 'sent' | 'delivered' | 'completed' | 'declined' | 'voided';

export interface PlanConversionsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  stage: PlanConversionStage;
  ftw_customer_id: string | null;
  ftw_plan_id: string | null;
  ftw_pushed_at: Nullable<Date>;
  docs_generated_at: Nullable<Date>;
  company_rewritten_at: Nullable<Date>;
  plan_renamed_at: Nullable<Date>;
  kpc_regenerated_at: Nullable<Date>;
  // Storage ref per package filename; null means no package was generated.
  package_storage_keys: NullableJson<Record<string, string>>;
  signed_docs_storage_key: string | null;
  signed_docs_filename: Nullable<string>;
  signed_at: Nullable<Date>;
  authorization_letter_generated_at: Nullable<Date>;
  authorization_letter_storage_key: Nullable<string>;
  signed_schwab_form_storage_key: Nullable<string>;
  certificate_storage_key: Nullable<string>;
  recordkeeper_change_generated_at: Nullable<Date>;
  recordkeeper_change_storage_key: Nullable<string>;
  // The corrected-date re-send of the Schwab Recordkeeper Change Form: its own
  // form, envelope, and access code, tracked apart from the signed package so
  // nothing here can move the conversion's signature state.
  rk_resend_form_generated_at: Nullable<Date>;
  rk_resend_form_storage_key: Nullable<string>;
  rk_resend_envelope_id: Nullable<string>;
  rk_resend_envelope_status: Nullable<DocusignEnvelopeStatus>;
  rk_resend_sent_at: Nullable<Date>;
  rk_resend_signer_email: Nullable<string>;
  rk_resend_access_code: Nullable<string>;
  rk_resend_signed_form_storage_key: Nullable<string>;
  error_message: string | null;
  started_by_user_id: string | null;
  docusign_envelope_id: string | null;
  // Envelopes deliberately abandoned by an admin un-sign; completion events
  // for these must never re-run the signed transition.
  unsigned_envelope_ids: string[];
  envelope_status: DocusignEnvelopeStatus | null;
  envelope_sent_at: Nullable<Date>;
  signer_name: string | null;
  signer_email: string | null;
  // When set, the signer-sheet allowlist no longer blocks this plan's envelope
  // send — an admin approved sending despite the plan missing from the sheet.
  signer_sheet_overridden_at: Nullable<Date>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export type OnboardingEnvelopeRecipientRole = 'sponsor' | 'kpc' | 'schwab';

export interface OnboardingEnvelopeRecipientStatus {
  role: OnboardingEnvelopeRecipientRole;
  name: string;
  email: string;
  status: string;
  completed_at: string | null;
}

export interface OnboardingEnvelopesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  signer_name: string | null;
  signer_email: string | null;
  // E.164; required before send — DocuSign's 2-step recipient auth for the
  // sponsor runs against this number.
  signer_phone: string | null;
  signer_title: string | null;
  package_storage_keys: NullableJson<Record<string, string>>;
  docusign_envelope_id: string | null;
  envelope_status: DocusignEnvelopeStatus | null;
  recipient_statuses: NullableJson<OnboardingEnvelopeRecipientStatus[]>;
  envelope_sent_at: Nullable<Date>;
  signed_docs_storage_key: string | null;
  signed_docs_filename: string | null;
  error_message: string | null;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

// New-plan onboarding FTW plan-document pipeline: step markers for the FTW
// company rewrite / plan push / document generation, and the sponsor-only
// adoption-agreement DocuSign envelope. The sponsor signer lives on the
// employer's onboarding_envelopes row, not here.
export interface OnboardingAdoptionAgreementsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  company_rewritten_at: Nullable<Date>;
  ftw_pushed_at: Nullable<Date>;
  ftw_customer_id: string | null;
  ftw_plan_id: string | null;
  docs_generated_at: Nullable<Date>;
  package_storage_keys: NullableJson<Record<string, string>>;
  docusign_envelope_id: string | null;
  envelope_status: DocusignEnvelopeStatus | null;
  envelope_sent_at: Nullable<Date>;
  signed_docs_storage_key: string | null;
  signed_docs_filename: string | null;
  error_message: string | null;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export interface PhoneContactsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  e164_phone: string;
  display_name: string | null;
  role: string | null;
  created_at: Timestamp;
}

export type RegulationBody =
  | 'irc'
  | 'erisa'
  | 'cfr_26'
  | 'cfr_29'
  | 'rev_proc'
  | 'rev_rul'
  | 'irs_notice'
  | 'dol_guidance'
  | 'irs_pub'
  | 'form_5500_instructions'
  | 'epcrs'
  | 'cola'
  | 'public_law';

export interface RegulationsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  body: RegulationBody;
  citation: string;
  title: string;
  source_url: string;
  effective_date: DateString | null;
  superseded_at: DateString | null;
  source_hash: string;
  created_at: Generated<Date>;
  updated_at: Generated<Date>;
}

export interface RegulationChunksRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  regulation_id: string;
  ordinal: number;
  chunk_text: string;
  // GENERATED ALWAYS tsvector over chunk_text; read-only.
  search: ColumnType<string | null, never, never>;
  created_at: Generated<Date>;
}

export const STAGE_COLUMNS = [
  'stage_payroll',
  'stage_recordkeeper',
  'stage_census',
  'stage_plan_rules',
  'stage_eligibility',
  'stage_compensation',
  'stage_reconciliation',
  'stage_hce',
  'stage_coverage_410b',
  'stage_adp_acp',
  'stage_limits_402g',
  'stage_additions_415',
  'stage_top_heavy',
  'stage_corrections',
  'stage_trust',
  'stage_participant_counts',
  'stage_form_assembly',
  'stage_filing',
] as const;

export type StageColumn = (typeof STAGE_COLUMNS)[number];

export type PayrollFetchRunStatus = 'running' | 'success' | 'failed';
export type PayrollFetchFailureKind = 'auth' | 'internal';
export type PayrollFetchTriggerKind = 'schedule' | 'manual' | 'verification';

// Secret fields end in `_ciphertext`; driver-side decrypt walks keys
// and remaps `<x>_ciphertext` back to `<x>` after decryption.
export type ConnectionPayload =
  | { kind: 'file_upload' }
  | { kind: 'oauth'; platform: 'gusto'; oauth_token_ciphertext: string }
  | {
      kind: 'login_credentials';
      platform: string;
      fields: { [name: string]: string };
    };

// Recordkeeper connection payload. `stub` is a placeholder kind for
// recordkeepers whose driver is registered but not yet functional —
// the picker filters them out, but the type is wired end-to-end so
// the integration concept exists in v1 (COG-87).
export type RecordkeeperConnectionPayload = { kind: 'file_upload' } | { kind: 'stub'; platform: string };

export type PayrollFetchSchedule =
  | { kind: 'every_n_days'; n_days: number }
  | { kind: 'weekly'; day_of_week: number }
  | { kind: 'first_of_month' }
  | { kind: 'last_of_month' }
  | { kind: 'day_x_of_month'; day_x: number };

type VerificationStatus = 'pending' | 'running' | 'success' | 'failed';
type VerificationFailureKind = 'auth' | 'internal';

export interface PayrollConnectionInfosRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  payload: Json<ConnectionPayload>;
  connected_by_sponsor_email: string | null;
  connected_by_user_id: string | null;
  last_success_at: Nullable<Date>;
  last_run_start_at: Nullable<Date>;
  failed_at: Nullable<Date>;
  consecutive_failures: Generated<number>;
  created_at: Timestamp;
  disconnected_at: Nullable<Date>;
  verification_status: VerificationStatus;
  verification_failure_kind: Generated<VerificationFailureKind | null>;
  verification_started_at: Nullable<Date>;
  verification_finished_at: Nullable<Date>;
}

export interface PayrollFetchRunsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  info_id: string;
  triggered_by: PayrollFetchTriggerKind;
  started_at: Timestamp;
  finished_at: Nullable<Date>;
  status: PayrollFetchRunStatus;
  failure_kind: Generated<PayrollFetchFailureKind | null>;
}

export interface RecordkeeperConnectionInfosRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  payload: Json<RecordkeeperConnectionPayload>;
  connected_by_sponsor_email: string | null;
  connected_by_user_id: string | null;
  last_success_at: Nullable<Date>;
  last_run_start_at: Nullable<Date>;
  failed_at: Nullable<Date>;
  consecutive_failures: Generated<number>;
  created_at: Timestamp;
  disconnected_at: Nullable<Date>;
  verification_status: VerificationStatus;
  verification_failure_kind: Generated<VerificationFailureKind | null>;
  verification_started_at: Nullable<Date>;
  verification_finished_at: Nullable<Date>;
}

export interface RecordkeeperFetchRunsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  info_id: string;
  triggered_by: PayrollFetchTriggerKind;
  started_at: Timestamp;
  finished_at: Nullable<Date>;
  status: PayrollFetchRunStatus;
  failure_kind: Generated<PayrollFetchFailureKind | null>;
}

// ---------------------------------------------------------------------------
// COG-113 — back-office admin + participant infrastructure
// ---------------------------------------------------------------------------

export interface BackOfficeAdminsRow {
  email: string;
  name: string;
  created_at: Timestamp;
}

export type BackOfficeSqlQueryStatus = 'running' | 'success' | 'failed' | 'blocked';

export interface BackOfficeSqlResultData {
  columns: string[];
  rows: Record<string, unknown>[];
}

export interface BackOfficeSqlQueriesRow {
  id: Generated<string>;
  created_at: Timestamp;
  admin_email: string;
  workspace_id: Nullable<string>;
  query: string;
  status: BackOfficeSqlQueryStatus;
  outcome_reason: Nullable<string>;
  row_count: Nullable<number>;
  response_bytes: Nullable<number>;
  duration_ms: Nullable<number>;
  result_json: NullableJson<BackOfficeSqlResultData>;
}

export interface ParticipantOtpsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  participant_id: string;
  email: string;
  code_bcrypt: string;
  created_at: Timestamp;
  expires_at: ColumnType<Date, Date, Date>;
  matched_at: Nullable<Date>;
  failed_attempts: Generated<number>;
}

// Pre-login brute-force guard for participant identify. Global (no workspace_id /
// RLS) — see migration 064 and check-rls.ts SKIP. attempt_indexes are opaque
// blind-index HMACs, not partial SSNs.
export interface ParticipantIdentifyAttemptsRow {
  identity_hash: string;
  attempt_indexes: string[];
  window_started_at: Generated<Date>;
  locked_until: Generated<Date | null>;
  updated_at: Generated<Date>;
}

// 3(16) ------------------------------------------------------------------

export type DefaultDeliveryMethod = 'email' | 'paper';

export interface Workspace316ConfigRow {
  workspace_id: string;
  admin_legal_name: string;
  admin_address: Json<MailingAddress>;
  admin_phone: string;
  agent_for_process_name: Generated<string | null>;
  agent_for_process_address: ColumnType<MailingAddress | null, string | null | undefined, string | null>;
  logo_storage_key: Generated<string | null>;
  default_delivery: DefaultDeliveryMethod;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export const NOTICE_TYPES = [
  'sar',
  'annual_safe_harbor',
  'qdia',
  '404a5_fee_disclosure',
  'auto_enrollment',
  'blackout',
  'force_out',
  'rmd_notification',
  'smm',
  'eligibility',
  'enrollment',
  'claim_decision',
  'claim_extension',
  'contact_capture_request',
  'other',
] as const;
export type NoticeType = (typeof NOTICE_TYPES)[number];

export type NoticeStatus = 'pending' | 'drafted' | 'sent' | 'skipped';

export type NoticeRecipientFilter =
  | { kind: 'all_active' }
  | { kind: 'all_eligible' }
  | { kind: 'all_participants' }
  | { kind: 'terminated' }
  | { kind: 'specific'; participant_ids: string[] };

// Unified notice lifecycle table — see migration 049 for the status
// state machine.
export interface NoticesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  notice_type: NoticeType;
  recipient_filter: Json<NoticeRecipientFilter>;
  window_start: DateString | null;
  window_end: DateString | null;
  status: NoticeStatus;
  pdf_storage_key: Generated<string | null>;
  email_subject: Generated<string | null>;
  email_body: Generated<string | null>;
  pdf_body: Generated<string | null>;
  notice_note: Generated<string | null>;
  recipient_count: Generated<number>;
  paper_certified: boolean;
  paper_return_receipt: boolean;
  // Set by the daily filer so a re-run collides instead of filing twice; null
  // on notices raised by a one-off action (a claim decision, an extension).
  dedupe_key: string | null;
  approved_at: Generated<Date | null>;
  approved_by_user_id: Generated<string | null>;
  sent_at: Generated<Date | null>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export type DeliveryChannel = 'email' | 'paper';
export type DeliveryStatus = 'queued' | 'sent' | 'delivered' | 'bounced' | 'returned' | 'failed';
export type DeliveryProvider = 'agentmail' | 'lob' | 'manual';
export type BounceState = 'open' | 'resolved';

export interface DeliveriesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  notice_id: string;
  employer_id: string;
  participant_id: string;
  channel: DeliveryChannel;
  destination: string;
  provider: DeliveryProvider;
  provider_message_id: string | null;
  status: DeliveryStatus;
  bounce_state: Generated<BounceState | null>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export type DeliveryEventType =
  | 'queued'
  | 'sent'
  | 'in_transit'
  | 'delivered'
  | 'bounced'
  | 'returned'
  | 'opened'
  | 'cure_step';

export interface DeliveryEventsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  delivery_id: string;
  event_type: DeliveryEventType;
  payload: Json<Record<string, unknown>>;
  occurred_at: Generated<Date>;
}

export type ClaimType =
  | 'distribution_request'
  | 'loan_request'
  | 'hardship_request'
  | 'eligibility_dispute'
  | 'other';

export type ClaimSource = 'portal' | 'email' | 'phy_mail';

export type ClaimStatus = 'received' | 'awaiting_decision' | 'decided' | 'appealed' | 'deemed_exhausted';

export type ProposedDecisionStatus = 'pending' | 'drafted' | 'failed';
export type DecisionOutcome = 'approved' | 'denied';

export const CLAIM_TRIGGER_SOURCES = [
  'denied_hardship',
  'denied_distribution',
  'complaint',
  'appeal',
  'incomplete',
  'manual_mark',
] as const;
export type ClaimTriggerSource = (typeof CLAIM_TRIGGER_SOURCES)[number];

// 'low' blocks any claim decision.
export const CLAIM_MATCH_CONFIDENCES = ['high', 'medium', 'low', 'unmatched'] as const;
export type ClaimMatchConfidence = (typeof CLAIM_MATCH_CONFIDENCES)[number];

export interface ClaimsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  participant_id: string;
  claim_type: ClaimType;
  description: string;
  source: ClaimSource;
  source_email_message_id: string | null;
  received_at: Generated<Date>;
  decision_deadline: ColumnType<Date, Date, Date>;
  status: ClaimStatus;
  claim_trigger_source: ClaimTriggerSource | null;
  match_confidence: ClaimMatchConfidence | null;
  required_documents: Json<string[]>;
  rm_approved_by_user_id: string | null;
  rm_approved_at: Generated<Date | null>;
  proposed_decision_text: string | null;
  proposed_decision_reasoning: string | null;
  proposed_decision_at: Generated<Date | null>;
  proposed_decision_status: ProposedDecisionStatus;
  decided_at: Generated<Date | null>;
  decided_by_user_id: string | null;
  decision_outcome: Generated<DecisionOutcome | null>;
  decision_notice_id: string | null;
  extension_count: Generated<number>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export type ClaimAppealStatus = 'received' | 'awaiting_decision' | 'decided';

export interface ClaimAppealsRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  claim_id: string;
  received_at: Generated<Date>;
  decision_deadline: ColumnType<Date, Date, Date>;
  status: ClaimAppealStatus;
  proposed_decision_text: string | null;
  proposed_decision_reasoning: string | null;
  proposed_decision_at: Generated<Date | null>;
  proposed_decision_status: ProposedDecisionStatus;
  decided_at: Generated<Date | null>;
  decided_by_user_id: string | null;
  decision_outcome: Generated<DecisionOutcome | null>;
  decision_notice_id: string | null;
  extension_count: Generated<number>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export type RtsSearchStatus = 'open' | 'closed_found' | 'closed_exhausted';

export type RtsSearchStepKind =
  | 'certified_remail'
  | 'check_related_records'
  | 'contact_beneficiary'
  | 'free_electronic_search'
  | 'paid_commercial_locator';

export interface RtsSearchStepTaken {
  step_kind: RtsSearchStepKind;
  taken_at: string;
  taken_by_user_id: string | null;
  result: 'success' | 'no_result' | 'skipped';
  notes: string | null;
}

export interface RtsSearchProposedStep {
  step_kind: RtsSearchStepKind;
  reasoning: string;
}

export interface RtsSearchesRow {
  id: Generated<string>;
  workspace_id: Generated<string>;
  employer_id: string;
  participant_id: string;
  triggering_delivery_id: string | null;
  status: RtsSearchStatus;
  steps_taken: Json<RtsSearchStepTaken[]>;
  proposed_next_step: ColumnType<RtsSearchProposedStep | null, string | null | undefined, string | null>;
  proposed_next_step_at: Generated<Date | null>;
  opened_at: Generated<Date>;
  closed_at: Generated<Date | null>;
  created_at: Timestamp;
  updated_at: Generated<Date>;
}

export type TodayOriginType =
  | 'proposed_action'
  | 'assigned_thread'
  | 'claim'
  | 'notice_draft'
  | 'delivery_bounce'
  | 'rts_search'
  | 'term_contact_capture'
  | 'work_item'
  | 'payroll_remediation'
  | 'shuttle_stopped'
  | 'shuttle_unmapped'
  | 'plan_doc_approval'
  | 'key_participant_unlinked'
  | 'ltpt_review'
  | 'unmatched_sender'
  // Synthetic, route-only: a per-notice-type roll-up of the far-future annual
  // notice backlog. Never emitted by the today_screen_items view.
  | 'notice_rollup';

export type TodayCriticality = 'HIGH' | 'MEDIUM' | 'LOW';

// SQL view. proposed_action_trigger / proposed_action_type / thread_id are
// populated only on origin_type='proposed_action' rows; NULL on every other branch.
export interface TodayScreenItemsView {
  origin_type: ColumnType<TodayOriginType, never, never>;
  origin_id: ColumnType<string, never, never>;
  employer_id: ColumnType<string | null, never, never>;
  workspace_id: ColumnType<string, never, never>;
  action_kind: ColumnType<string, never, never>;
  criticality: ColumnType<TodayCriticality, never, never>;
  created_at: ColumnType<Date, never, never>;
  deadline: ColumnType<Date | null, never, never>;
  summary: ColumnType<string | null, never, never>;
  proposed_action_trigger: ColumnType<string | null, never, never>;
  proposed_action_type: ColumnType<string | null, never, never>;
  thread_id: ColumnType<string | null, never, never>;
  assigned_user_id: ColumnType<string | null, never, never>;
}
